import {
  IDeadReceiverEvidence,
  IStalledVideoEvidence,
  TRTCConnection,
  TRTCState,
  TSequencedDescription,
  TStreamUpdatePayload,
} from './types';
import { VOIP_CONFIG } from 'config/voip';
import { State } from 'utils/state';
import { IRtcClient } from './types';
import { EConnectionEvents, ERTCEvents, EWebsocketClientEvents } from './events';
import { ISignalClient, TRawConnection } from './signal/types';
import { SignalerClient } from './signal';
import { applyVideoSendLimits } from './videoSendLimits';
import { myCameraStream } from 'state/video';
import { recordSocketGaveUp, recordSocketReconnectAttempt } from 'state/session/syncTelemetry';
import {
  recordVoipIceRestart,
  recordVoipIceRestartGiveUp,
  recordVoipPeerWrittenOff,
  recordVoipTransportRebuild,
} from 'state/session/voipQuality';
import log from 'utils/log';
import { EventEmitter, Unsubscribe } from 'utils/events';
import { SpeakerDetector } from 'utils/ui/speakerDetector';
import { VoipMediaState } from 'utils/ui/media';
import { filterUsableIceServers, getIceServersExpiry } from 'utils/ui/iceServers';
import { inboundCountersByTrack, isWebKitEngine, mutedReceiverTrackIds, risingMutedKinds } from 'utils/ui/webkit';
import { mirrorableMlines } from './mlineLayout';

const defaultRTCState: TRTCState = {
  me: {
    connectionId: '',
  },
  remoteConnections: {},
};

/**
 * Sink for the Session Flight Recorder (AVS-4430).
 *
 * MODULE scope, not per-instance, for the same reason `setSignalFrameSink` is: `RTCClient.getInstance`
 * builds a NEW client after `destroy()` and `utils/ui/rtc.ts:26` reassigns its module binding on
 * takeover, so an instance-level hook goes blind exactly during the outage being recorded. Nothing
 * here may be keyed on `window.voipRtcConnection` either — that global is assigned only AFTER a
 * successful connect (`utils/ui/rtc.ts:713`), so anything reading it misses every connect FAILURE.
 *
 * A SINK, never a listener: `utils/events.ts:239` gives a listener's return value the
 * prevent-execution protocol, and `utils/ui/media.ts:144` aborts `getUserMedia` on a falsy one — a
 * lane wired as a listener would disable the camera it exists to observe. {@link reportRtc}
 * therefore returns `void` and discards whatever the sink returns.
 *
 * `payload` carries LIVE objects (an `RTCIceCandidate`, an `RTCSessionDescriptionInit`, a
 * `MediaStream`). This module deliberately applies no privacy policy to them: SDP and ICE
 * candidates carry IP addresses and the tiering that decides which of those survive lives with the
 * recorder (`telemetry/rtcStats.ts`), so this file keeps ZERO telemetry imports and stays absent
 * from the recorder's build gate.
 */
export type TRtcEventSink = (type: string, payload: Record<string, unknown>) => void;

let rtcEventSink: TRtcEventSink | null = null;

/** Register the recorder's RTC sink. `null` restores the no-op. Idempotent by assignment. */
export function setRtcEventSink(sink: TRtcEventSink | null): void {
  rtcEventSink = sink;
}

/**
 * Hand one observation to the sink, isolated.
 *
 * The sink is registered from outside and arbitrary: a throwing one must never fail a WebRTC call.
 * `console.error` rather than `log.*` matches `utils/events.ts:165-169` — several suites hand-mock
 * `utils/log` with a partial object, and an isolation path that itself throws is not an isolation
 * path. The try block holds only the sink call.
 */
function reportRtc(type: string, payload: Record<string, unknown>): void {
  if (!rtcEventSink) {
    return;
  }

  try {
    rtcEventSink(type, payload);
  } catch (error) {
    console.error('[VOIP] RTC recorder sink threw and was isolated', error);
  }
}

/** Which negotiation pass an RTCClient instance has run. See `RTCClient.lastNegotiationPass`. */
export type TNegotiationPass = 'none' | 'publish' | 'receive-only';

/**
 * Why a peer's RTCPeerConnection was replaced — reported on `transport-rebuild`. `dtls-failed` and
 * `stalled-transport` re-negotiate from scratch; `offer-rejected` (AVS-4465) answers the offer the
 * old pc refused.
 */
type TTransportRebuildReason =
  'dtls-failed' | 'stalled-transport' | 'offer-rejected' | 'offer-collision' | 'dead-receiver';

// AVS-4484: replacements that keep off the shared transport budget. Each has its own per-peer counter
// owned by its caller, so a real DTLS failure later still finds both rebuilds and a spent budget can
// never write off a peer that still carries audio.
const BUDGET_EXEMPT_REBUILD_REASONS: ReadonlySet<TTransportRebuildReason> = new Set([
  'offer-collision',
  'dead-receiver',
]);

// AVS-4484: the one-shot check that runs after a WebKit rollback-and-apply. It remembers the pc it was
// armed on and the inbound counters read at that moment, so a later read can prove growth.
/** The inbound counters of one pc: kind-wide totals for the evidence, and one counter per receiver track. */
type TInboundCounters = { audioBytes: number; frames: number; byTrack: ReadonlyMap<string, number> };

type TDeadReceiverCheck = {
  peerConnection: RTCPeerConnection;
  timer: ReturnType<typeof setTimeout>;
  baseline?: TInboundCounters;
};

/**
 * A stopped transceiver carries nothing. `direction` covers one stopped before it was ever
 * negotiated, whose currentDirection stays null.
 */
function isStopped(transceiver: RTCRtpTransceiver): boolean {
  return transceiver.direction === 'stopped' || transceiver.currentDirection === 'stopped';
}

/** Runs `work`, returning its rejection instead of throwing (`null` on success). */
async function rejectionOf(work: () => Promise<unknown>): Promise<Error | null> {
  try {
    await work();
    return null;
  } catch (error) {
    return error instanceof Error ? error : new Error(String(error));
  }
}

export class RTCClient extends EventEmitter implements IRtcClient {
  private static instance: RTCClient | null = null;

  private wsEndpoint: string;

  private signalerClient!: ISignalClient;

  private iceServers: RTCIceServer[] = [];

  // AVS-4391: when the cached TURN credentials die, so that createPeerConnection can say so
  // instead of silently building a peer that will gather no relay candidates. Null = unknown.
  private iceServersExpireAt: number | null = null;
  /** One report and one toast per client for a refusal that belongs to the browser, not the peer. */
  private peerConnectionUnavailableReported = false;

  private published: boolean;

  // AVS-4302: true once this client has run a receive-only negotiation pass (a standby).
  // `ConnectionAdded` reads it so peers that join AFTER negotiateAsReceiver() also negotiate
  // media-free — without it, a late-joining peer would take the default (publishing) path and
  // a standby holding a camera-grab-race stream could leak a second dealer stream. Reset to
  // false by publish(), so a standby promoted to master on the same instance shares its media.
  private receiveOnly: boolean = false;

  // AVS-4363 (T7b): which negotiation pass this instance has run. Written ONLY by publish()
  // and negotiateAsReceiver(), cleared by destroy() — never by the media-policy flags above.
  private negotiationPass: TNegotiationPass = 'none';

  private pendingCandidates: Record<string, RTCIceCandidate[]> = {};

  // AVS-4465: signaling does not keep message order, so every SDP this client sends is numbered.
  // One counter for all peers, only ever rising and never reset: a fresh client or a reconnected
  // socket has a new connectionId, so a receiver's mark for us starts over by itself, while a reset
  // on a surviving id would make that receiver drop every later offer we send.
  private sdpSeq = 0;

  // AVS-4465: the highest `seq` seen from each peer. An inbound offer below it is one the peer has
  // already rolled back or superseded (see acceptSequencedDescription). Cleared with the peer's
  // other state by destroy, WsClientReconnected and removePeer — but deliberately KEPT across a
  // transport rebuild: the peer's counter keeps rising, and its pre-rebuild stale offers must still drop.
  private highestSdpSeq: Record<string, number> = {};

  // AVS-4442: per-peer count of divergent-answer re-offers sent, bounding the stable-case heal below.
  private divergentHeals: Record<string, number> = {};

  // AVS-4302 perfect-negotiation glare resolution (keyed by peer connectionId).
  // makingOffer: true while our own createOffer/setLocalDescription is in flight, so a colliding
  // inbound offer is detected even before signalingState flips to have-local-offer.
  private makingOffer: Record<string, boolean> = {};

  // ignoringOffer: true after we (the impolite peer) dropped a colliding offer; gates ICE
  // candidate-error swallowing to that discarded offer ONLY. Cleared the moment the surviving
  // negotiation reaches stable (an accepted offer, or our own kept offer getting answered).
  private ignoringOffer: Record<string, boolean> = {};

  // AVS-4302 perfect-negotiation recovery (keyed by peer connectionId).
  // renegotiationTimers: per-peer "awaiting answer" timer, armed when we send an offer and cleared
  // when the pair reaches stable (onsignalingstatechange). On expiry the answer never came, so we
  // RE-OFFER (forceReoffer) — never roll back, so a slow/surviving offer is never corrupted.
  private renegotiationTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  // renegotiationRetries: bounds forceReoffer so a permanently unanswerable peer cannot loop forever.
  private renegotiationRetries: Record<string, number> = {};

  // iceGraceTimers: a single grace window per peer for a transient ICE 'disconnected' before we
  // escalate to restartIce. iceRestartCounts bounds the restarts so a dead peer cannot churn.
  private iceGraceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  private iceRestartCounts: Record<string, number> = {};

  // AVS-4430: latches the ICE-restart give-up so the recorder hears about an exhausted budget
  // EXACTLY ONCE per exhaustion. maybeRestartIce is re-entered on every subsequent ICE 'failed'
  // event for the same dead peer, and an unlatched event would bury the one moment that matters
  // under a repeating line. Cleared wherever iceRestartCounts is — a returned budget is a fresh
  // ladder, and a second genuine exhaustion is a second genuine event.
  private iceGiveUpReported: Record<string, boolean> = {};

  // AVS-4311 follow-up: bounds rebuildPeerTransport. A DTLS transport failure (connectionState
  // 'failed' while ICE is NOT 'failed') is fatal to the RTCPeerConnection — restartIce() cannot
  // revive it — so recovery replaces the pc entirely. The cap stops a permanently dead peer from
  // rebuild-looping.
  private transportRebuilds: Record<string, number> = {};

  // AVS-4441: how many times the silent-transport watchdog has escalated this peer. Deliberately
  // NOT refilled by handleIceConnectionStateChange alongside the three budgets above: when an ICE
  // restart flaps through 'connected' on its way to carrying nothing again, a refill there would
  // let a permanently silent peer restart ICE every window for the whole call. Only a fresh
  // transport (rebuildPeerTransport), a departure (removePeer) or a session reset refills it. The
  // ladder restarts ICE on this budget alone and never touches iceRestartCounts (AVS-4458).
  private silentTransportEscalations: Record<string, number> = {};

  // AVS-4458: the stalled-video ladder's budget per peer, and the latch that says its give-up (the
  // camera-off request) already went out. Same no-refill rule as silentTransportEscalations — an
  // ICE restart can flap through 'connected' while the video stays undecodable. Refilled by a recovery
  // (clearStalledVideo), the peer turning its camera back on, a fresh transport, a departure, or a
  // session reset.
  private stalledVideoEscalations: Record<string, number> = {};

  private stalledVideoGiveUpSent: Record<string, true> = {};

  // AVS-4484: WebKit answers a first-negotiation collision on a fresh pc. This counts those replacements
  // per peer so an offer storm cannot churn pcs without bound; it is NOT cleared when the pc is replaced
  // (that is the point), only by a departure, a reconnect or a session reset. The latch keeps the
  // exhaustion report to one per peer.
  private collisionReplacements: Record<string, number> = {};

  private collisionExhaustedReported: Record<string, true> = {};

  private readonly maxCollisionReplacements = 3;

  // AVS-4484: the dead-receiver ladder's per-peer budget and its give-up latch. Like the collision
  // counter they survive pc replacement (the rebuild they cause must not refill them) and are released
  // only by a departure, a reconnect or a session reset. Kept off `transportRebuilds` so a real DTLS
  // failure later still has both of its rebuilds.
  private deadReceiverRebuilds: Record<string, number> = {};

  private deadReceiverGiveUpSent: Record<string, true> = {};

  private readonly maxDeadReceiverRebuilds = 2;

  // AVS-4484: the one-shot check armed by a WebKit rollback-and-apply, one per peer. `Pending` means
  // the check found negotiation busy (or had no baseline yet) and waits for the pc to be stable again;
  // `Attempts` bounds those re-arms per rollback. All three are released by `clearDeadReceiverCheck`,
  // which removePeer, destroy, the reconnect reset and replacePeerConnection all call.
  private deadReceiverChecks: Record<string, TDeadReceiverCheck> = {};

  private deadReceiverCheckPending: Record<string, true> = {};

  private deadReceiverCheckAttempts: Record<string, number> = {};

  private readonly deadReceiverCheckMs = 2000;

  private readonly maxDeadReceiverCheckRearms = 3;

  // AVS-4458: peers whose camera-off request this tab already honoured since its user last turned
  // the camera on through toggleMedia. One honoured request per peer per local unmute, so a peer
  // cannot hold this camera off against its user.
  private videoOffHonored: Record<string, true> = {};

  // AVS-4440: the last `mutedSeq` this client stamped on a muted-state update. Only rises, so a
  // receiver can drop an update that signaling delivered after a later one.
  private lastMutedSeq = 0;

  // AVS-4302 takeover reliability: per-peer telemetry deadline armed by every negotiation pass.
  // If the pair has not reached connectionState 'connected' by then, ONE structured warn names
  // the peer and its signaling/ICE/stream state — the field failures this covers are rare and
  // Safari-skewed, and undiagnosable without a snapshot of the stuck peer.
  private convergenceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  private readonly convergenceReportMs = 8000;

  // AVS-4302 takeover reliability: an offer can legitimately beat its ConnectionAdded (the Add
  // burst is async and unordered vs peer offers), so such peers are admitted on demand
  // (metaPending). The cap stops a spoofed-offer flood from creating unbounded pcs.
  private readonly maxMetaPendingPeers = 8;

  private readonly renegotiationTimeoutMs = 5000;

  // AVS-4302 takeover reliability: deadline for the WAITING (non-initiator) side of a pair that
  // never negotiated. 2× the offerer's timeout so the initiator's own recovery gets the first
  // shot before the waiting side risks a (glare-safe) collision.
  private readonly waitingOfferTimeoutMs = 10000;

  private readonly maxRenegotiationRetries = 4;

  private readonly iceDisconnectGraceMs = 5000;

  private readonly maxIceRestarts = 3;

  private readonly maxTransportRebuilds = 2;

  // AVS-4441: silent-transport windows a peer gets before it is written off — two, like
  // maxTransportRebuilds (maxIceRestarts is three). At ~20 s per window the peer is written
  // off around a minute after the media stopped — fast enough that the tile is dropped rather than
  // frozen, slow enough that a long stall in a recovering network is not mistaken for a dead one.
  private readonly maxSilentTransportEscalations = 2;

  // AVS-4458: ICE restarts the stalled-video ladder spends before its give-up — two, like the silent
  // ladder, so the camera-off request goes out about a minute after the video stopped decoding.
  private readonly maxStalledVideoEscalations = 2;

  // AVS-4392: candidates rejected by the current transport are retained for retry, so the queue
  // needs a ceiling — a candidate that can never apply would otherwise accumulate for the peer's
  // whole lifetime. Drops oldest-first (cf. maxMetaPendingPeers, the same bounded-resource idea).
  private readonly maxPendingCandidates = 50;

  public store: State<TRTCState>;

  public sessionId?: string;

  // AVS-4461: stamped by `initWebRtc` when this client was built without the tab's own camera and
  // mic — a standby, or a master claim still pending the "Take over" click. Reset by destroy().
  public withoutOwnMedia = false;

  // AVS-4461: true once connect() resolved; reset by destroy(). `initWebRtc` installs the client
  // and its sessionId BEFORE awaiting connect(), so a matching sessionId alone does not mean the
  // connection works.
  private connectResolved = false;

  private listeners: Unsubscribe[] = [];

  private speakerDetector: SpeakerDetector;

  private registerStreamRetryCount: number = 0;

  private readonly maxRegisterStreamRetries: number = 2;

  private constructor(iceServers: RTCIceServer[], wsEndpoint: string) {
    super();
    // AVS-4391: the caller has just fetched this list, so now is its fetch time.
    this.updateIceServers(iceServers);
    this.wsEndpoint = wsEndpoint;
    this.store = new State<TRTCState>(defaultRTCState);
    this.published = false;
    this.speakerDetector = new SpeakerDetector({
      threshold: 0.03,
      interval: 200,
      speakingTimeout: 2000,
    });
  }

  // Singleton instance accessor with lazy initialization
  public static getInstance(iceServers: RTCIceServer[], wsEndpoint: string): RTCClient {
    if (!RTCClient.instance || wsEndpoint !== RTCClient.instance.wsEndpoint) {
      RTCClient.instance = new RTCClient(iceServers, wsEndpoint);
    } else {
      // AVS-4391: the reuse path used to DISCARD the freshly fetched credential list, pinning
      // the surviving instance to TURN credentials that expire ~5 min after ITS construction.
      RTCClient.instance.updateIceServers(iceServers);
    }

    return RTCClient.instance;
  }

  /**
   * The live singleton, or `null` when none is constructed.
   *
   * AVS-4430: the recorder's stats sampler reads this on EVERY tick and never caches the result.
   * `getInstance` builds a new client after `destroy()` (which nulls this), and `utils/ui/rtc.ts:26`
   * reassigns its own module binding on takeover — a cached instance would sample a dead client for
   * the rest of the call. Unlike `getInstance` this never constructs, so an observer can ask
   * "is there a client?" without becoming the reason there is one.
   */
  public static current(): RTCClient | null {
    return RTCClient.instance;
  }

  /**
   * AVS-4391: replace the cached ICE server list so every FUTURE RTCPeerConnection — a late
   * joiner, a rejoin, an AVS-4311 transport rebuild — gathers with live TURN credentials
   * (KVS credentials expire after Ttl, default 300s; rtc.ts refreshes them on a timer).
   * Existing pcs keep their config: ICE servers apply at construction time only.
   *
   * `fetchedAtMs` anchors the expiry used for the staleness warning below. It defaults to now,
   * which is right for every caller (each one has just fetched the list); rtc.ts passes its own
   * pre-dispatch stamp so a slow response cannot make the credentials look fresher than they are.
   *
   * AVS-4418: the list is sanitised HERE, the one place a fetched list enters the client, so every
   * pc, the expiry below, the `pc-created` telemetry and the refresh scheduler (via
   * `iceServerList`) all see the same entries. An entry the RTCPeerConnection constructor refuses
   * — no urls (the `{}` an older lambda serialises when KVS returns nothing), a turn url without
   * credentials — used to fail EVERY peer construction for this client. Only counts are logged: a
   * dropped entry may still carry a credential, and the rtc lane applies no redaction.
   *
   * `iceServers` is `unknown` because that is what it really is: every list reaching here came
   * from `sendApiRequest`, which casts a parsed body to its type parameter without checking it.
   * Typing the parameter `RTCIceServer[]` would only restate the server's claim and make the
   * guard below look unreachable to a reader.
   */
  public updateIceServers(iceServers: unknown, fetchedAtMs: number = Date.now()): void {
    const usable = filterUsableIceServers(iceServers);
    if (!Array.isArray(iceServers)) {
      // Unreachable from our lambda, but the refresh scheduler calls this outside its try and a
      // throw here would leave the loop dead for the rest of the call.
      log.warn('[VOIP] ICE server list is not an array; peers will be built without ICE servers', typeof iceServers);
    } else if (usable.length < iceServers.length) {
      log.warn(
        '[VOIP] Dropped malformed ICE server entries; peers will be built from the rest',
        iceServers.length - usable.length
      );
    }
    this.iceServers = usable;
    this.iceServersExpireAt = getIceServersExpiry(usable, fetchedAtMs);
  }

  /**
   * The list every future peer connection is built from — sanitised (AVS-4418). The refresh
   * scheduler in utils/ui/rtc.ts schedules off THIS, never the raw body it fetched, so the two can
   * never disagree about when the credentials die.
   */
  public get iceServerList(): RTCIceServer[] {
    return this.iceServers;
  }

  /** AVS-4461: whether this client's connect() has resolved (and it was not destroyed since). */
  public get connected(): boolean {
    return this.connectResolved;
  }

  public async connect(): Promise<void> {
    this.connectResolved = false;
    try {
      this.signalerClient = new SignalerClient(this.wsEndpoint);
      const connection = await this.signalerClient.connect();
      this.setupListeners();
      this.store.setValues({
        me: {
          ...connection,
        },
      });
      this.connectResolved = true;
      this.emit('voip:connected', connection);
      log.log('[VOIP] Signaling Client Connected', connection, Date.now().toString().slice(-5));
      // Attempt to stream the user's camera and microphone. In most cases this will be too early and won't happen.
      this.registerOutgoingStream();
    } catch (error) {
      log.log(
        '[VOIP] Unable to connect to the stream. Destroying signaler client.',
        error,
        Date.now().toString().slice(-5)
      );
      if (this.signalerClient) this.signalerClient.disconnect();
      this.connectResolved = false;
      throw new Error('RTC Client Connection Failure', { cause: error });
    }
  }

  public registerOutgoingStream() {
    const stream = myCameraStream().stream;
    const me = this.store.get('me') ?? {};
    log.log('[VOIP] Registering Outgoing Stream', stream, me, Date.now().toString().slice(-5));
    if (stream) {
      this.store.setValues({
        me: {
          ...me,
          stream,
        },
      });
      this.shareMyStream();

      log.log('[VOIP] Your stream is published', Date.now().toString().slice(-5));
      // Reset retry count on successful registration
      this.registerStreamRetryCount = 0;
    } else {
      // No local stream available - user is in receive-only mode
      // This is a valid state (e.g., when camera/mic permissions are denied)
      log.log('[VOIP] No outgoing stream to register - receive-only mode', Date.now().toString().slice(-5));
      this.registerStreamRetryCount = 0;
    }
  }

  registerMyVideoStreamListeners() {
    // AVS-4440: a second registration must replace the first, or every flag change fans out twice.
    this.listeners.forEach((unListen) => unListen());
    this.listeners = [];

    const unListenTrackAdded = myCameraStream().on('track:added', (track?: MediaStreamTrack) => {
      if (!track) return;
      this.addMyTrackToAllPeers(track);
    });

    const unListenTrackRemoved = myCameraStream().on('track:stopped', (track: MediaStreamTrack) => {
      this.removeMyTrackFromAllPeers(track);
    });

    // AVS-4440: every enabled-flag write is a wire event — the toggle, a failed toggle's rollback and
    // the grab-time init alike — so peers never keep a stale mute badge. Keyed on the FLAG, not on
    // `track:stopped`, which fires inside `enableTrack` before the flag flips.
    const unListenFlags = myCameraStream().on('change', (changedKeys: string[]) => {
      if (!changedKeys.includes('isAudioEnabled') && !changedKeys.includes('isVideoEnabled')) return;
      this.signalMutedStateToAllPeers();
    });

    this.listeners = [unListenTrackAdded, unListenTrackRemoved, unListenFlags];
  }

  public destroy(): void {
    const { me, remoteConnections } = this.store.data;

    log.log('[VOIP] Destryoing Stream', Date.now().toString().slice(-5), remoteConnections);

    // AVS-4430: the takeover boundary. `getInstance` builds a NEW client after this, so a recording
    // that does not mark the seam reads the two clients' peers as one confusing sequence.
    reportRtc('client-destroyed', {
      sessionId: this.sessionId,
      peers: Object.keys(remoteConnections),
      published: this.published,
      receiveOnly: this.receiveOnly,
      negotiationPass: this.negotiationPass,
      hadLocalStream: !!me.stream,
    });

    if (this.signalerClient) this.signalerClient.disconnect();

    // AVS-4302: cancel every pending recovery timer BEFORE closing pcs, and null the pc event
    // handlers so a close-induced state change cannot arm a post-teardown timer or re-offer.
    Object.values(this.renegotiationTimers).forEach((timer) => clearTimeout(timer));
    Object.values(this.iceGraceTimers).forEach((timer) => clearTimeout(timer));
    Object.values(this.convergenceTimers).forEach((timer) => clearTimeout(timer));
    Object.values(this.deadReceiverChecks).forEach((check) => clearTimeout(check.timer));

    Object.values(remoteConnections).forEach(({ peerConnection }) => {
      if (peerConnection) {
        peerConnection.onnegotiationneeded = null;
        peerConnection.onsignalingstatechange = null;
        peerConnection.oniceconnectionstatechange = null;
        peerConnection.onicecandidate = null;
        peerConnection.ontrack = null;
        peerConnection.onconnectionstatechange = null;
        peerConnection.close();
      }
    });

    this.store.setValues(defaultRTCState);

    // Drop the local-stream track listeners BEFORE releasing the camera, so the release's
    // `track:stopped` does not bounce back through `removeMyTrackFromAllPeers` during teardown.
    this.listeners.forEach((unListen) => unListen());
    this.listeners = [];

    // AVS-4302: release the local camera THROUGH its manager (which clears the reference and
    // notifies consumers) instead of a raw `track.stop()` that leaves `myCameraStream().stream`
    // pointing at a DEAD stream. That dead stream was reused after a reconnect/step-down (and in
    // the minimized pill) and rendered BLACK until a manual video cycle re-grabbed it — because
    // the re-grab guards (`!myCameraStream().stream`, `if (myCameraStream().stream) return`) saw a
    // non-null-but-dead stream and skipped re-acquisition. Clearing the reference makes the next
    // init() / takeover grab a fresh, live camera. Only when this client OWNS that stream (a
    // publisher registered it as `me.stream`): a receive-only standby never registered one — and a
    // gesture-acquired takeover camera (me.stream still unset) must survive the destroy untouched.
    const localCamera = myCameraStream();
    if (me.stream && me.stream === localCamera.stream) {
      localCamera.stopStream();
    } else {
      me.stream?.getTracks().forEach((track: MediaStreamTrack) => track.stop());
    }

    this.speakerDetector.stop();

    // Reset retry count on destroy
    this.registerStreamRetryCount = 0;

    // AVS-4302: make destroy() a real teardown so a takeover destroy→reconnect cycle
    // is leak-free and a genuine re-init is not short-circuited.
    // - Null the singleton: getInstance (L59) otherwise returns this dead instance for
    //   an unchanged wsEndpoint.
    // - Reset sessionId: rtc.ts's `voipRtcConnection?.sessionId === sessionId` early-return
    //   reads the OLD instance until the next getInstance reassigns it; clearing sessionId
    //   stops that check from matching and short-circuiting a real re-init.
    // - Reset published so a reconnected client re-publishes from scratch.
    this.published = false;
    this.receiveOnly = false;
    this.negotiationPass = 'none';
    this.sessionId = undefined;
    this.withoutOwnMedia = false;
    this.connectResolved = false;
    this.makingOffer = {};
    this.ignoringOffer = {};
    this.highestSdpSeq = {};
    this.divergentHeals = {};
    this.renegotiationTimers = {};
    this.renegotiationRetries = {};
    this.iceGraceTimers = {};
    this.iceRestartCounts = {};
    this.iceGiveUpReported = {};
    this.transportRebuilds = {};
    this.silentTransportEscalations = {};
    this.stalledVideoEscalations = {};
    this.stalledVideoGiveUpSent = {};
    this.collisionReplacements = {};
    this.collisionExhaustedReported = {};
    this.deadReceiverRebuilds = {};
    this.deadReceiverGiveUpSent = {};
    this.deadReceiverChecks = {};
    this.deadReceiverCheckPending = {};
    this.deadReceiverCheckAttempts = {};
    this.videoOffHonored = {};
    this.convergenceTimers = {};
    RTCClient.instance = null;
  }

  /**
   * Which negotiation pass (`publish()` / `negotiateAsReceiver()`) this instance last ran.
   * Lives on the instance so the trigger in `useVoipConnections` survives a React remount
   * against a still-live connection (hook refs reset on every mount and would re-offer to
   * every peer); `destroy()` resets it, so a genuine rebuild re-runs its pass. Deliberately
   * not derived from `published`/`receiveOnly` — those have a third writer
   * (`liftIncomingMediaSuppression`), and a derived guard would skip a promoted master's
   * `publish()`.
   */
  public get lastNegotiationPass(): TNegotiationPass {
    return this.negotiationPass;
  }

  public async publish(): Promise<void> {
    this.published = true;
    this.negotiationPass = 'publish';
    // A publisher shares media with every peer, including late-joiners (ConnectionAdded). Clear
    // the standby's receive-only flag so a standby promoted to master on the same instance is
    // not stuck negotiating media-free.
    this.receiveOnly = false;
    // Re-enable incoming tracks a prior receive-only pass disabled (same-instance promotion).
    this.applyIncomingMediaPolicyToAll();
    const remoteConnections = this.store.get('remoteConnections');

    log.log('[VOIP] Publishing Stream', Date.now().toString().slice(-5), remoteConnections);

    Object.keys(remoteConnections).forEach((connectionId) => {
      this.prepareConnectionAndOffer(connectionId);
    });

    this.speakerDetector.start();
  }

  /**
   * AVS-4302: a standby must not PLAY incoming media (participants would be heard/seen in
   * every tab). While `receiveOnly` is set, every received track is disabled — RTP keeps
   * flowing so a takeover restores instantly, with no renegotiation. Bypassed under the
   * `loopbackVideo` debug flag, which exists to inspect live loopback streams.
   */
  private get suppressIncomingMedia(): boolean {
    return this.receiveOnly && !VOIP_CONFIG.loopbackVideo;
  }

  // Optional-call guard instead of `instanceof MediaStream`: policy must also cover
  // reconstructed/foreign stream objects that carry tracks without the prototype.
  private applyIncomingMediaPolicy(stream?: MediaStream): void {
    const enabled = !this.suppressIncomingMedia;
    stream?.getTracks?.().forEach((track) => {
      track.enabled = enabled;
    });
  }

  private applyIncomingMediaPolicyToAll(): void {
    const remoteConnections = this.store.get('remoteConnections') ?? {};
    Object.values(remoteConnections).forEach(({ stream }) => this.applyIncomingMediaPolicy(stream));
  }

  /**
   * AVS-4302: takeover pre-rebuild unsuppression. `performVoipTakeover` flips `isVoipStandby`
   * synchronously (local `voipMasters` write) but only destroys this client AFTER the network
   * flush — without this lift, remote tiles mount `<video>` with still-disabled tracks for
   * ~1 RTT (a black+silent flash). Lifting here lets the still-connected old instance play
   * live media until the rebuilt client takes over.
   */
  public liftIncomingMediaSuppression(): void {
    this.receiveOnly = false;
    this.applyIncomingMediaPolicyToAll();
  }

  /**
   * AVS-4302: in-place demotion teardown. A previously-publishing instance that flips to
   * receive-only (an `<e3d-voip>` embed has no step-down rebuild) must stop transmitting:
   * null every active sender (keeps the transceiver reusable, same rationale as
   * `removeMyTrackFromAllPeers`) and release the camera so its light goes off. The camera is
   * released through its manager only when this client owns it (see `destroy()`), and
   * `me.stream` is cleared so a later same-instance promotion cannot re-send a dead stream.
   */
  private stopPublishingMedia(): void {
    const { me, remoteConnections } = this.store.data;

    Object.values(remoteConnections).forEach(({ connectionId, peerConnection }) => {
      peerConnection?.getSenders?.().forEach((sender) => {
        if (sender.track) {
          log.log('[VOIP] Demotion: muting sender on', connectionId, Date.now().toString().slice(-5));
          sender.replaceTrack(null).catch((err: unknown) => {
            log.log('[VOIP] Demotion replaceTrack(null) failed for', connectionId, err);
          });
        }
      });
    });

    const localCamera = myCameraStream();
    if (me.stream && me.stream === localCamera.stream) {
      localCamera.stopStream();
    } else {
      me.stream?.getTracks?.().forEach((track: MediaStreamTrack) => track.stop());
    }
    this.store.setValues({ me: { ...me, stream: undefined } });
  }

  /**
   * AVS-4302: a standby publishes no media (a standby never grabs the camera — see
   * `initWebRtc`), but it MUST still drive the offer/answer handshake as the lexical
   * initiator. Otherwise a peer pair where the standby owns the higher `connectionId`
   * never negotiates: the master is not the initiator, and the standby never ran an offer
   * pass — so the master's loopback stream never reaches the standby until an unrelated
   * mute/unmute on the master forces an out-of-band offer (the reported bug: the standby's
   * "You" tile shows a placeholder, then snaps to video the instant the master toggles).
   *
   * This mirrors how a receive-only customer participates via `publish()`. Passing
   * `receiveOnly` keeps every offer media-free, so a standby can never accidentally publish
   * a second dealer stream. Future peers are handled by the `ConnectionAdded` listener
   * (guarded by `this.published`), so this only needs to run once per client instance.
   */
  public negotiateAsReceiver(): void {
    // An in-place demotion (was publishing on THIS instance) must stop transmitting too.
    const wasPublishing = this.published && !this.receiveOnly;
    this.published = true;
    this.negotiationPass = 'receive-only';
    // Force the recvonly path for current AND future peers (ConnectionAdded reads this.receiveOnly).
    this.receiveOnly = true;
    if (wasPublishing) {
      this.stopPublishingMedia();
    }
    // Tracks can arrive BEFORE this pass (a standby answers inbound offers ungated), so
    // sweep everything already stored — ontrack covers the rest.
    this.applyIncomingMediaPolicyToAll();
    const remoteConnections = this.store.get('remoteConnections');

    log.log('[VOIP] Negotiating receive-only (standby)', Date.now().toString().slice(-5), remoteConnections);

    Object.keys(remoteConnections).forEach((connectionId) => {
      this.prepareConnectionAndOffer(connectionId, true);
    });

    // Suppressed media is silent — polling analysers would burn CPU for nothing.
    if (!this.suppressIncomingMedia) {
      this.speakerDetector.start();
    }
  }

  private setupListeners(): void {
    // AVS-4332: socket-health telemetry for the MEDIA/VOIP socket, counted separately from the
    // session-broadcast socket so one number can never hide which channel died.
    //
    // Telemetry ONLY. Degraded session sync is a session-state-sync concept owned by the broadcast
    // socket in `utils/ui/rtc.ts`; the media channel dying must never flip session sync into
    // degraded mode. Nothing here may touch `syncStatus` / the degraded poll.
    this.signalerClient.on(EWebsocketClientEvents.WsClientReconnecting, () => {
      recordSocketReconnectAttempt('voip');
    });

    this.signalerClient.on(EWebsocketClientEvents.WsClientGaveUp, () => {
      recordSocketGaveUp('voip');
    });

    this.signalerClient.on(EWebsocketClientEvents.WsClientReconnected, (newConnection: TRawConnection) => {
      const me = this.store.get('me');
      // Update our connection ID — the old one is no longer valid on the server
      this.store.setValues({
        me: { ...me, connectionId: newConnection.connectionId },
      });
      // Close and clear stale peer connections.
      // The server re-dispatches ConnectionAdded for each active peer so RTCClient
      // will create fresh RTCPeerConnections via the normal ConnectionAdded handler.
      const staleConnections = this.store.get('remoteConnections');
      Object.values(staleConnections).forEach(({ peerConnection }) => {
        peerConnection?.close();
      });
      this.store.setValues({ remoteConnections: {} });
      this.pendingCandidates = {};
      // AVS-4302: a reconnect reassigns me.connectionId (the polite/impolite role can flip) and
      // tears down peers; a stale makingOffer/ignoringOffer would misclassify the first
      // post-reconnect offer and re-deadlock. Clear alongside pendingCandidates. The recovery timers
      // are keyed by the OLD (now-invalid) connectionIds, so cancel and empty the whole maps.
      this.makingOffer = {};
      this.ignoringOffer = {};
      // AVS-4465: every peer comes back under a new connectionId, so the old marks are dead keys.
      this.highestSdpSeq = {};
      this.divergentHeals = {};
      Object.values(this.renegotiationTimers).forEach((timer) => clearTimeout(timer));
      Object.values(this.iceGraceTimers).forEach((timer) => clearTimeout(timer));
      Object.values(this.convergenceTimers).forEach((timer) => clearTimeout(timer));
      Object.values(this.deadReceiverChecks).forEach((check) => clearTimeout(check.timer));
      this.renegotiationTimers = {};
      this.renegotiationRetries = {};
      this.iceGraceTimers = {};
      this.iceRestartCounts = {};
      this.iceGiveUpReported = {};
      this.transportRebuilds = {};
      this.silentTransportEscalations = {};
      this.stalledVideoEscalations = {};
      this.stalledVideoGiveUpSent = {};
      this.collisionReplacements = {};
      this.collisionExhaustedReported = {};
      this.deadReceiverRebuilds = {};
      this.deadReceiverGiveUpSent = {};
      this.deadReceiverChecks = {};
      this.deadReceiverCheckPending = {};
      this.deadReceiverCheckAttempts = {};
      this.videoOffHonored = {};
      this.convergenceTimers = {};
      log.log(
        '[VOIP] Signaling reconnected, cleared stale peer connections',
        newConnection,
        Date.now().toString().slice(-5)
      );
    });

    this.signalerClient.on(EConnectionEvents.ConnectionAdded, ({ connectionId, meta }: TRawConnection) => {
      log.log('[VOIP] Connection Adding - ', connectionId, Date.now().toString().slice(-5));
      const remoteConnections = this.store.get('remoteConnections');
      const existing = remoteConnections[connectionId];

      // A live entry is MERGED, never rebuilt: an on-demand (metaPending) peer already negotiated
      // on this pc, and a duplicate Add would otherwise leak the old pc and wipe stream/mute state
      // (live tile → placeholder). peer:connected fires once — when the identity first lands.
      if (existing?.peerConnection) {
        const wasPending = existing.metaPending;
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [connectionId]: { ...existing, meta, metaPending: undefined },
          },
        });
        if (wasPending) {
          this.emit('peer:connected', connectionId, this.store.get('remoteConnections'));
        }
        log.log('[VOIP] Connection Added (merged) - ', connectionId, Date.now().toString().slice(-5));
        return;
      }

      // AVS-4430 media-health: a remote appears with NO MediaStream — the gray tile, recorded the
      // moment it appears rather than inferred later from its absence. `stream-attached` closes it.
      reportRtc('peer-added', { connectionId, hasStream: false, metaPending: false, source: 'connection-added' });
      const peerConnection = this.createPeerConnection(connectionId);
      // A browser that will not build peer connections builds none for anyone. Storing the peer
      // without one would leave a permanently gray tile and a signaling loop with nothing to feed.
      if (!peerConnection) return;
      this.store.setValues({
        remoteConnections: {
          ...this.store.get('remoteConnections'),
          [connectionId]: { connectionId, meta, peerConnection },
        },
      });
      if (this.published) this.prepareConnectionAndOffer(connectionId, this.receiveOnly);
      this.emit('peer:connected', connectionId, this.store.get('remoteConnections'));
      log.log('[VOIP] Connection Added - ', connectionId, Date.now().toString().slice(-5));
    });

    this.signalerClient.on(EConnectionEvents.ConnectionRemoved, ({ connectionId }: TRawConnection) => {
      // Only announce the departure of a peer we actually tracked. A tab learns peers from an async
      // ConnectionAdded burst (the handshake carries no peer list — see connect()), so during takeover
      // churn or after a signaling reconnect cleared the store it can legitimately receive a
      // ConnectionRemoved for a connectionId it never stored. With no store entry there is no identity
      // (meta) to tell whether it was THIS dealer's own tab, and we never showed a "peer connected"
      // toast for it — so a "peer disconnected" toast would be a false positive (AVS-4302). Return
      // before removePeer so the untracked path never reaches speakerDetector.stop().
      const tracked = this.store.get('remoteConnections')[connectionId];
      if (!tracked) return;
      this.removePeer(connectionId);
      // An identity-pending peer never announced a connect — announcing its disconnect would be
      // a false "peer disconnected" toast (own-tab churn can't be classified without meta).
      if (tracked.metaPending) return;
      // AVS-4392: a peer the recovery ladder wrote off was already announced by markTransportFailed;
      // its eventual real departure must not toast the customer a second time for the same peer.
      if (tracked.transportFailed) return;
      this.emit('peer:disconnected', connectionId, this.store.get('remoteConnections'), tracked.meta);
    });

    this.signalerClient.on(ERTCEvents.PeerSignal, this.onPeerSignal.bind(this));

    this.signalerClient.on(ERTCEvents.StreamUpdate, (data: TStreamUpdatePayload, from?: string) => {
      const remoteConnections = this.store.get('remoteConnections');
      const remoteConnection = remoteConnections[from!];
      if (remoteConnection) {
        log.log(`[VOIP] Remote stream update - ${from}`, data);
        // AVS-4392: `transportFailed` is a LOCAL observation about OUR transport to this peer, so
        // the wire must never set or clear it — this handler spreads whatever arrives, which would
        // otherwise let a peer hide itself from us, or resurrect a tile we already proved dead.
        // AVS-4458: `videoStalled` is local for the same reason, and `videoOffRequest` is a one-shot
        // command, not state — neither may land on the entry.
        const safeData = { ...data };
        delete safeData.transportFailed;
        delete safeData.videoStalled;
        delete safeData.videoOffRequest;
        // AVS-4440: order the muted state. A `mutedSeq` that is not a finite number is treated as
        // absent (an older build sends none), so it can never overwrite the stored one. A finite
        // one at or below the stored value is late, and its muted flags are dropped. Everything
        // below reads `safeData`, so a dropped flag touches neither the ladder nor `videoStalled`.
        const mutedSeq: unknown = safeData.mutedSeq;
        if (typeof mutedSeq !== 'number' || !Number.isFinite(mutedSeq)) {
          delete safeData.mutedSeq;
        } else if (remoteConnection.mutedSeq !== undefined && remoteConnection.mutedSeq >= mutedSeq) {
          delete safeData.audioMuted;
          delete safeData.videoMuted;
          delete safeData.mutedSeq;
        }
        // AVS-4458: the peer turned its camera back on, so its stalled-video ladder starts over —
        // the product's "fresh budget". Only the true→false transition counts, and the watchdog
        // escalates at most once per window, so `videoMuted` spam cannot buy extra restarts.
        if (remoteConnection.videoMuted === true && safeData.videoMuted === false) {
          delete this.stalledVideoEscalations[from!];
          delete this.stalledVideoGiveUpSent[from!];
        }
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [from!]: {
              ...remoteConnection,
              ...safeData,
              // A camera that is off cannot be stalled; its tile shows the avatar now.
              ...(safeData.videoMuted === true ? { videoStalled: false } : {}),
            },
          },
        });
        if (data.videoOffRequest === true) {
          this.handleVideoOffRequest(from!);
        }
      }
    });

    this.speakerDetector.on('activeSpeakerChanged', (speakerId: string | null) => {
      this.handleActiveSpeakerChange(speakerId);
    });
  }

  private async onPeerSignal(signal: TSequencedDescription | RTCIceCandidate, from?: string) {
    let data: RTCSessionDescriptionInit | RTCIceCandidate = signal;
    let superseded = false;
    if ('type' in signal) {
      const accepted = this.acceptSequencedDescription(signal as TSequencedDescription, from);
      if (!accepted) {
        return;
      }
      ({ description: data, superseded } = accepted);
    }
    const remoteConnections = this.store.get('remoteConnections');
    let connection: TRTCConnection | undefined = remoteConnections[from!];

    if (!connection?.peerConnection) {
      // AVS-4302 takeover reliability: an offer from an unknown peer means its ConnectionAdded is
      // late (async burst) or lost — dropping it strands the pair, because the peer's bounded
      // re-offers all hit this same guard. Admit the peer on demand; ICE/answers from unknown
      // peers are meaningless without prior negotiation state and stay dropped.
      connection = 'type' in data && data.type === 'offer' && from ? this.admitPeerFromOffer(from) : undefined;
      if (!connection) {
        console.warn('[VOIP] Peer Connection not found for ', from);
        return;
      }
    }
    // If the data contains a `type` property, it's an SDP (offer or answer)
    if ('type' in data) {
      switch (data.type) {
        case 'offer':
          log.log('[VOIP] Received an offer from ', from, data, Date.now().toString().slice(-5));
          await this.handleOfferSignals(data, from);
          break;
        case 'answer':
          log.log('[VOIP] Received an answer from ', from, data, Date.now().toString().slice(-5));
          await this.handleAnswerSignals(data, from, superseded);
          break;
        default:
          console.warn('[VOIP] Received an unknown signal type from ', from, data);
      }
    } else {
      // AVS-4302: await so pending ICE candidates are flushed only after remoteDescription is set.
      await this.handleIceCandidate(data, from);
    }

    // AVS-4392: the flush is genuinely async now, and this handler's own promise floats in the
    // signaler — so contain the rejection here rather than letting it escape unhandled.
    void this.handlePendingIceCandidates(from).catch((error: unknown) => {
      log.warn('[VOIP] Pending ICE candidate flush failed', from, error);
    });
  }

  /**
   * AVS-4465: strip `seq` from an inbound SDP, and drop an offer that signaling delivered late.
   *
   * API Gateway does not keep message order. In session 3551deda the customer's offer (sent 32.280)
   * reached the standby after the customer's answer (sent 32.451): the customer had rolled that
   * offer back to answer ours, but the standby was stable again and applied it. A lower `seq` than
   * one already seen from this peer means the peer produced a later description since — so an offer
   * below the mark was rolled back or superseded, and applying it can only break the pair.
   *
   * Answers only raise the mark: the stale-answer guard in handleAnswerSignals owns them. A missing
   * or malformed `seq` (an older client mid-deploy, or garbage) is treated as absent and handled as
   * before — it can only affect the sender's own pair, since the server sets `from`.
   *
   * Returns the description without `seq`, or `null` when it must be dropped. Built field by field,
   * like the outbound copy, so it never depends on how the object was made. `superseded` (AVS-4442)
   * says the peer already sent a description at least this new, so this one is late or a duplicate.
   */
  private acceptSequencedDescription(
    description: TSequencedDescription,
    from?: string
  ): { description: RTCSessionDescriptionInit; superseded: boolean } | null {
    const stripped: RTCSessionDescriptionInit = { type: description.type, sdp: description.sdp };
    const { seq } = description;
    if (!from || typeof seq !== 'number' || !Number.isSafeInteger(seq) || seq < 0) {
      return { description: stripped, superseded: false };
    }
    const highest = this.highestSdpSeq[from];
    if (description.type === 'offer' && highest !== undefined && seq < highest) {
      log.warn('[VOIP] Dropping an offer the peer already superseded (signaling reordered it)', from, {
        seq,
        highest,
      });
      reportRtc('sdp-stale-dropped', { connectionId: from, seq, highest });
      return null;
    }
    this.highestSdpSeq[from] = highest === undefined ? seq : Math.max(highest, seq);
    return { description: stripped, superseded: highest !== undefined && seq <= highest };
  }

  /**
   * AVS-4465: send a local description to `to`, numbered for acceptSequencedDescription on the far
   * side. Every offer and answer goes out through here. Field by field, never a spread: Chromium's
   * createOffer resolves an RTCSessionDescription whose `type` and `sdp` are prototype getters, and
   * `{ ...offer }` is `{}`.
   */
  private signalDescription(to: string, description: RTCSessionDescriptionInit): void {
    this.sdpSeq += 1;
    const data: TSequencedDescription = { type: description.type, sdp: description.sdp, seq: this.sdpSeq };
    this.signalerClient.signal({ action: ERTCEvents.PeerSignal, to: [to], data });
  }

  /**
   * AVS-4302 takeover reliability: create a peer entry for an inbound offer that beat (or lost)
   * its ConnectionAdded. The entry is `metaPending` — fully negotiable but identity-less: the
   * render layer skips it and no peer toast fires until ConnectionAdded merges the real meta in.
   * When this client already ran its per-peer negotiation pass (published), run it for the
   * admitted peer too so our senders attach BEFORE we answer — otherwise the on-demand pair
   * stays recvonly-forever on our side and the peer never sees our media.
   */
  private admitPeerFromOffer(connectionId: string): TRTCConnection | undefined {
    const pendingCount = Object.values(this.store.get('remoteConnections')).filter((c) => c.metaPending).length;
    if (pendingCount >= this.maxMetaPendingPeers) {
      log.warn('[VOIP] Refusing on-demand peer admission (cap reached)', connectionId, Date.now().toString().slice(-5));
      return undefined;
    }
    log.log('[VOIP] Offer from unknown peer — admitting on demand', connectionId, Date.now().toString().slice(-5));
    reportRtc('peer-added', { connectionId, hasStream: false, metaPending: true, source: 'on-demand-offer' });
    const peerConnection = this.createPeerConnection(connectionId);

    if (!peerConnection) {
      return undefined;
    }

    this.store.setValues({
      remoteConnections: {
        ...this.store.get('remoteConnections'),
        [connectionId]: { connectionId, metaPending: true, peerConnection },
      },
    });
    if (this.published) {
      void this.prepareConnectionAndOffer(connectionId, this.receiveOnly);
    }
    return this.store.get('remoteConnections')[connectionId];
  }

  private async handleIceCandidate(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      return;
    }

    // Handle ICE candidates
    reportRtc('ice-candidate', { connectionId: from, direction: 'remote', candidate: data });
    if (connection.peerConnection.remoteDescription) {
      const { peerConnection } = connection;
      const failure = await rejectionOf(() => peerConnection.addIceCandidate(data as RTCIceCandidate));
      if (!failure) return;
      // AVS-4302: while we are ignoring a colliding offer from this peer, its in-flight
      // candidates belong to the DISCARDED offer — they are dead, not early, so they are
      // swallowed and must never be queued for retry. The flag is cleared once the surviving
      // negotiation reaches stable.
      if (this.ignoringOffer[from!]) {
        log.log('[VOIP] Swallowed ICE candidate for an ignored offer', from, Date.now().toString().slice(-5));
        return;
      }
      // The transport can be REPLACED while we await (rebuildPeerTransport swaps the pc and clears
      // this queue; removePeer drops the entry). Queueing now would insert a dead-transport
      // candidate into the FRESH pc's queue, where the flush's identity guard can no longer catch
      // it (current === the new pc) — retained and retried for the peer's whole lifetime.
      if (this.store.get('remoteConnections')[from!]?.peerConnection !== connection.peerConnection) {
        log.log('[VOIP] Dropped an ICE candidate for a replaced transport', from, Date.now().toString().slice(-5));
        return;
      }
      // AVS-4392 B1: during an ICE RESTART `remoteDescription` is the STALE previous answer, so
      // this branch is taken while the peer's new answer is still in flight and the candidate is
      // rejected against the old ufrag. Queue it — rethrowing here (the old behaviour) surfaced as
      // an unhandled rejection in an async void handler and lost the candidate permanently, which
      // alone can stop ICE completing: the far side's checks stay NAT-filtered because we never
      // send checks toward candidates we never stored.
      log.log('[VOIP] Queueing ICE candidate rejected by the current transport', from, failure.message);
    }
    this.queuePendingCandidate(data as RTCIceCandidate, from);
  }

  /**
   * AVS-4392: bounded per-peer candidate queue. A candidate that can never apply (malformed, or
   * gathered for a transport that has since been replaced) would otherwise be retried on every
   * subsequent signal for the peer's whole lifetime, so the queue drops oldest-first at the cap.
   */
  private queuePendingCandidate(candidate: RTCIceCandidate, from?: string) {
    if (!this.pendingCandidates[from!]) {
      this.pendingCandidates[from!] = [];
    }
    const queue = this.pendingCandidates[from!];
    queue.push(candidate);
    if (queue.length === this.maxPendingCandidates) {
      log.warn('[VOIP] Pending ICE candidate queue at capacity', from, Date.now().toString().slice(-5));
    }
    if (queue.length > this.maxPendingCandidates) {
      queue.splice(0, queue.length - this.maxPendingCandidates);
      log.log('[VOIP] Dropped oldest pending ICE candidate (queue full)', from, Date.now().toString().slice(-5));
    }
  }

  private async handlePendingIceCandidates(from?: string) {
    if (!from) {
      // Await the fan-out so this promise means "every peer was flushed", not "every flush was
      // started"; the per-peer catch keeps one failure from aborting the rest.
      await Promise.all(
        Object.keys(this.pendingCandidates).map((connectionId) =>
          this.handlePendingIceCandidates(connectionId).catch((error: unknown) => {
            log.warn('[VOIP] Pending ICE candidate flush failed', connectionId, error);
          })
        )
      );
      return;
    }
    log.log('[VOIP] Handling Pending ICE Candidates', from, Date.now().toString().slice(-5));

    const peerConnection = this.store.get('remoteConnections')[from]?.peerConnection;
    const candidates = this.pendingCandidates[from];

    if (!peerConnection?.remoteDescription || !candidates?.length) {
      return;
    }

    // AVS-4392: take the batch BEFORE awaiting so a candidate arriving mid-flush is not added
    // twice, and await each add — the old `forEach(async …)` never awaited, so every rejection
    // became an unhandled rejection and the queue was cleared whether or not the adds landed.
    this.pendingCandidates[from] = [];
    const stillPending: RTCIceCandidate[] = [];
    for (const candidate of candidates) {
      const failure = await rejectionOf(() => peerConnection.addIceCandidate(candidate));
      if (failure) stillPending.push(candidate);
    }
    if (!stillPending.length) return;

    // The transport can be REPLACED while we await (rebuildPeerTransport swaps the pc and clears
    // this queue; removePeer drops the entry entirely). Re-queueing candidates gathered for the
    // dead transport would poison the fresh one forever — they can never apply to it — and would
    // resurrect the queue of a departed peer. Re-read at fire time and drop the batch if it moved.
    const current = this.store.get('remoteConnections')[from]?.peerConnection;
    if (current !== peerConnection) {
      log.log('[VOIP] Dropped pending ICE candidates for a replaced transport', from, stillPending.length);
      return;
    }
    this.pendingCandidates[from] = [...stillPending, ...this.pendingCandidates[from]].slice(-this.maxPendingCandidates);
    log.warn('[VOIP] Retained ICE candidates the current transport rejected', from, stillPending.length);
  }

  private async handleAnswerSignals(
    data: RTCSessionDescriptionInit | RTCIceCandidate,
    from?: string,
    superseded = false
  ) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      console.warn('[VOIP] Peer Connection not found for answer from', from);
      return;
    }

    const signalingState = connection.peerConnection.signalingState;

    switch (signalingState) {
      case 'stable': {
        if (superseded) {
          console.warn('[VOIP] Received an answer in stable state, ignoring:', from, data);
          break;
        }
        // Our own offer is already in flight and will converge the pair; driveNegotiation would bail,
        // so do not spend a heal on it.
        if (this.makingOffer[from!]) {
          log.log('[VOIP] Answer in stable while our offer is in flight; letting it settle', from);
          break;
        }
        // AVS-4442: bound the heals below. forceReoffer's own retry budget resets on every return
        // to stable (clearRenegotiationTimer), so a peer that keeps answering slower than the
        // renegotiation timer would otherwise chain this re-offer forever.
        const heals = this.divergentHeals[from!] ?? 0;
        if (heals >= this.maxRenegotiationRetries) {
          log.warn('[VOIP] Giving up divergent-answer re-offers', from, heals);
          reportRtc('divergent-answer-give-up', { connectionId: from, heals });
          break;
        }
        this.divergentHeals[from!] = heals + 1;
        // AVS-4442: a fresh answer we hold no offer for — the peer applied one we rolled back. Both
        // ends report stable on different offer/answer pairs and nothing renegotiates; re-offer once.
        log.warn('[VOIP] Answer in stable for an offer we no longer hold; re-offering', from);
        reportRtc('sdp-rejected', { connectionId: from, direction: 'remote', reason: 'answer-in-stable-divergent' });
        void this.driveNegotiation(from!).catch((error: unknown) => {
          log.warn('[VOIP] Re-offer after a divergent answer failed', from, error);
        });
        break;
      }
      case 'have-local-offer':
        log.log('[VOIP] Received a have-local-offer answer from ', from, data, Date.now().toString().slice(-5));
        // AVS-4302 (review gate C1): guard against a STALE answer — one generated against an offer we
        // have already superseded (a recovery re-offer raced an in-flight answer). setRemoteDescription
        // rejects on the m-line/ufrag mismatch; without this guard the throw escapes onPeerSignal
        // uncaught. Swallow it and arm recovery instead — the pending newer offer's own answer (or the
        // timer) resolves the pair.
        try {
          await connection.peerConnection.setRemoteDescription(data as RTCSessionDescriptionInit);
        } catch (error) {
          log.warn('[VOIP] Ignoring stale/mismatched answer; arming recovery', from, error);
          reportRtc('sdp-rejected', { connectionId: from, direction: 'remote', reason: String(error) });
          this.armRenegotiationTimer(from!);
          break;
        }
        reportRtc('sdp', { connectionId: from, direction: 'remote', description: data });
        // AVS-4302: our own (kept) offer was answered — this negotiation is the survivor, so
        // stop swallowing ICE candidate errors for this peer.
        this.ignoringOffer[from!] = false;
        break;
      default:
        console.warn('[VOIP] Received an unknown answer signal type from ', from, data);
    }
  }

  private handleActiveSpeakerChange(speakerId: string | null): void {
    const remoteConnections = this.store.get('remoteConnections');

    // Reset all connections to not be active speakers and mark the last active speaker
    const updatedConnections = Object.keys(remoteConnections).reduce(
      (acc, connectionId) => {
        const connection = remoteConnections[connectionId];

        acc[connectionId] = {
          ...connection,
          isActiveSpeaker: connectionId === speakerId,
          lastActiveSpeaker: connection.isActiveSpeaker && !speakerId,
        };
        return acc;
      },
      {} as Record<string, TRTCConnection>
    );

    this.store.setValues({
      remoteConnections: updatedConnections,
    });
  }

  private startStreamingMe(peerConnection: RTCPeerConnection, placed?: ReadonlySet<MediaStreamTrack>) {
    const stream = myCameraStream().stream;

    if (!stream) {
      return false;
    }

    stream.getTracks().forEach((track: MediaStreamTrack) => {
      if (placed?.has(track)) return;
      this.sendTrackToPeer(track, peerConnection);
    });
  }

  private sendTrackToPeer(track: MediaStreamTrack, peerConnection: RTCPeerConnection): boolean {
    if (!myCameraStream().stream) {
      return false;
    }
    if (track.readyState === 'ended') {
      log.log('[VOIP] Skipping ended track', track.kind, Date.now().toString().slice(-5));
      return false;
    }
    log.log('[VOIP] Adding track to peer connection', track, peerConnection, Date.now().toString().slice(-5));
    if (track.kind === 'audio' || track.kind === 'video') {
      const senders = peerConnection.getSenders();
      const sender = senders.find((sender) => sender.track?.kind === track.kind);
      if (!sender) {
        // Look for a reusable transceiver (sender nulled via replaceTrack(null) during mute, or a
        // recvonly one from ensureReceiveTransceivers / a remote offer), never a stopped one.
        const reusableTransceiver = peerConnection
          .getTransceivers()
          .find((t) => t.sender.track === null && t.receiver.track?.kind === track.kind && !isStopped(t));
        if (reusableTransceiver) {
          log.log('[VOIP] Reusing existing sender for', track.kind, Date.now().toString().slice(-5));
          reusableTransceiver.sender.replaceTrack(track);
          applyVideoSendLimits(reusableTransceiver.sender, track);
          // If the transceiver direction doesn't allow sending (e.g. recvonly from
          // ensureReceiveTransceivers), update it and signal that renegotiation is needed.
          if (reusableTransceiver.direction !== 'sendrecv' && reusableTransceiver.direction !== 'sendonly') {
            // Associate track with a stream so the remote ontrack event includes event.streams[0].
            // Without this, replaceTrack on a reused transceiver produces msid:- in SDP.
            if (typeof reusableTransceiver.sender.setStreams === 'function') {
              reusableTransceiver.sender.setStreams(myCameraStream().stream!);
            }
            reusableTransceiver.direction = 'sendrecv';
            return true; // Renegotiation needed to enable sending
          }
          return false; // Direction already allows sending, no renegotiation needed
        } else {
          applyVideoSendLimits(peerConnection.addTrack(track, myCameraStream().stream!), track);
          return true; // New transceiver → needs offer
        }
      } else {
        sender.replaceTrack(track);
        applyVideoSendLimits(sender, track);
        return false; // No renegotiation needed
      }
    }
    return false;
  }

  private async sendOffer(peerConnection: RTCPeerConnection, connectionId: string) {
    log.log('[VOIP] Sending Offer to ', connectionId, Date.now().toString().slice(-5));

    // AVS-4302: flag our offer in flight so a colliding inbound offer is detected even before
    // signalingState flips to have-local-offer. The finally wraps BOTH createOffer and
    // setLocalDescription so an early throw can never strand the flag true (which would make us
    // wrongly treat the next inbound offer as a collision and re-deadlock).
    this.makingOffer[connectionId] = true;
    // AVS-4484: the peer's pc can be REPLACED while an await below is pending (a WebKit first-negotiation
    // collision, a rebuild). Everything after an await then belongs to a closed pc: it must not signal an
    // obsolete offer, arm a timer, or release the latch the replacement's answer is holding.
    const isCurrentPc = () => this.store.get('remoteConnections')[connectionId]?.peerConnection === peerConnection;
    try {
      // Inside the makingOffer window: the negotiationneeded that addTransceiver queues must see
      // this offer in flight (driveNegotiation bails), or it would race a second offer.
      this.ensureReceiveTransceivers(peerConnection);
      // Create an SDP offer to initiate the connection
      const offer = await peerConnection.createOffer();
      if (!isCurrentPc()) return;
      await peerConnection.setLocalDescription(offer);
      if (!isCurrentPc()) return;
      reportRtc('sdp', { connectionId, direction: 'local', description: offer });

      // Send the SDP offer to the remote peer via the signaling server
      this.signalDescription(connectionId, offer);
      // AVS-4302: we are now awaiting an answer. Arm the recovery timer so a lost/dropped answer
      // (the takeover-glare strand) re-offers instead of stranding at have-local-offer forever. The
      // timer is cleared the moment we reach stable (onsignalingstatechange), so a normally-answered
      // offer never fires it.
      this.armRenegotiationTimer(connectionId);
    } catch (error) {
      // A closed, replaced pc rejects its pending operations; that is not this offer's failure to report.
      if (isCurrentPc()) throw error;
      log.log('[VOIP] Offer abandoned: the peer connection was replaced', connectionId);
    } finally {
      if (isCurrentPc()) this.makingOffer[connectionId] = false;
    }
  }

  /**
   * AVS-4465: give the offer a media section for every kind we can receive.
   *
   * The legacy `createOffer({ offerToReceiveAudio, offerToReceiveVideo })` options used to do this.
   * Safari 26.6 ignores them, so a pc with no transceivers — a receive-only standby, or a customer
   * without a camera — offered an SDP with NO m-lines (prelive session 3551deda: every empty offer
   * came from the Safari standby tab). The customer then rolled back and re-offered with fresh mids,
   * and the pair never converged until ICE failed and the transport was rebuilt.
   *
   * A recvonly transceiver per missing kind is what Chromium already built from the legacy options,
   * so this changes nothing for Chromium clients. A stopped transceiver carries nothing and does not
   * count. `sendTrackToPeer` later reuses these transceivers when a track arrives (promotion, a late
   * camera), flipping them to sendrecv instead of adding a second m-line.
   */
  private ensureReceiveTransceivers(peerConnection: RTCPeerConnection): void {
    const transceivers = peerConnection.getTransceivers();
    (['audio', 'video'] as const).forEach((kind) => {
      const hasKind = transceivers.some((t) => t.receiver.track?.kind === kind && !isStopped(t));
      if (!hasKind) {
        peerConnection.addTransceiver(kind, { direction: 'recvonly' });
      }
    });
  }

  // AVS-4302: arm/clear the per-peer "awaiting answer" recovery timer. Re-arming first clears any
  // pending timer so we never stack two for one peer.
  private armRenegotiationTimer(connectionId: string): void {
    if (this.renegotiationTimers[connectionId]) {
      clearTimeout(this.renegotiationTimers[connectionId]);
    }
    this.renegotiationTimers[connectionId] = setTimeout(() => {
      void this.forceReoffer(connectionId);
    }, this.renegotiationTimeoutMs);
  }

  private clearRenegotiationTimer(connectionId: string): void {
    if (this.renegotiationTimers[connectionId]) {
      clearTimeout(this.renegotiationTimers[connectionId]);
      delete this.renegotiationTimers[connectionId];
    }
    this.renegotiationRetries[connectionId] = 0;
  }

  // AVS-4302 takeover reliability: the waiting (non-initiator) side of a never-negotiated pair
  // arms this deadline — a lost initiator offer otherwise strands the pair at 'stable' forever,
  // because only sendOffer arms recovery. Shares renegotiationTimers so every existing cleanup
  // path (destroy, reconnect reset, removePeer, transport rebuild, stable-reached) covers it,
  // and never replaces an already-armed timer (an offer of our own is already being recovered).
  private armWaitingOfferWatchdog(connectionId: string): void {
    if (this.renegotiationTimers[connectionId]) return;
    this.renegotiationTimers[connectionId] = setTimeout(() => {
      delete this.renegotiationTimers[connectionId];
      void this.offerIfNeverNegotiated(connectionId);
    }, this.waitingOfferTimeoutMs);
  }

  // AVS-4302 takeover reliability: telemetry deadline (see convergenceTimers). Re-armed by every
  // negotiation pass and by every ICE restart the stall ladder issues (AVS-4441); self-clearing on
  // fire, and cleared wherever the peer's other timers are.
  private armConvergenceReport(connectionId: string): void {
    this.clearConvergenceTimer(connectionId);
    this.convergenceTimers[connectionId] = setTimeout(() => {
      delete this.convergenceTimers[connectionId];
      this.reportConvergence(connectionId);
    }, this.convergenceReportMs);
  }

  private clearConvergenceTimer(connectionId: string): void {
    if (this.convergenceTimers[connectionId]) {
      clearTimeout(this.convergenceTimers[connectionId]);
      delete this.convergenceTimers[connectionId];
    }
  }

  private reportConvergence(connectionId: string): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const peerConnection = entry?.peerConnection;
    if (!entry || !peerConnection) return;
    // Transport up = negotiation worked; an absent stream on a connected pair is a legitimate
    // media decision (e.g. a peer publishing nothing), not a stall — stay silent for it.
    if (peerConnection.connectionState === 'connected') return;
    // AVS-4430: this payload already was exactly what a recorder wants — connectionId, all three
    // states, hasStream, per-track kind/enabled/muted, metaPending — and it only ever reached a
    // console that nobody kept. It is assembled ONCE and goes to both sinks: the warn stays because
    // it is what a live debugging session reads, and the record is what survives the tab closing.
    const convergence = {
      connectionId,
      signalingState: peerConnection.signalingState,
      connectionState: peerConnection.connectionState,
      iceConnectionState: peerConnection.iceConnectionState,
      hasStream: !!entry.stream,
      tracks:
        entry.stream
          ?.getTracks?.()
          .map((track) => ({ kind: track.kind, enabled: track.enabled, muted: track.muted })) ?? [],
      metaPending: !!entry.metaPending,
    };
    log.warn('[VOIP] Peer not converged after negotiation deadline', convergence);
    reportRtc('convergence-failed', convergence);
    this.escalateStalledTransport(connectionId);
  }

  /**
   * AVS-4441: put a pair that finished its handshake and never started its transport onto the
   * recovery ladder.
   *
   * The field signature (Audi Switzerland, 2026-09-09) was signalingState 'stable',
   * connectionState 'new', iceConnectionState 'new', zero bytes ever. Every ladder in this class
   * keys on 'failed' or 'disconnected'; this pair reached neither, so `handleIceConnectionStateChange`
   * fired no branch at all and the tile sat there until the dealer reloaded. The DETECTOR already
   * existed — the convergence deadline above — it simply had no actor.
   *
   * Three gates keep healthy pairs off the ladder, because 'new'/'new' is also what a perfectly
   * normal handshake measures before the first remote candidate arrives:
   *
   * - `published`: this client has not negotiated anything yet, so there is nothing to restart.
   * - `signalingState !== 'stable'`: an offer is still in flight; restarting now fights it.
   * - `remoteDescription === null`: nothing ever arrived — that is the LOST-OFFER case, which
   *   {@link armWaitingOfferWatchdog} owns on a timer a rebuild here would silently clear.
   */
  private escalateStalledTransport(connectionId: string): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const pc = entry?.peerConnection;
    if (!entry || !pc) return;
    if (entry.transportFailed) return;
    if (pc.connectionState === 'connected') return;
    if (!this.published) return;
    if (pc.signalingState !== 'stable') return;
    if (pc.remoteDescription === null) return;
    // The ICE agent must still be at 'new'. That is the field signature, and it is the ONLY ICE
    // state without a ladder: 'failed' and 'disconnected' are handled by
    // handleIceConnectionStateChange, and 'checking' always resolves into one of them. Without
    // this gate a slow-but-working TURN pair still negotiating at the 8 s deadline would get its
    // candidate pairs thrown away and restarted — the deadline is a stall detector, not a
    // patience limit.
    if (pc.iceConnectionState !== 'new') return;

    reportRtc('stalled-transport-escalation', {
      connectionId,
      signalingState: pc.signalingState,
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      iceRestarts: this.iceRestartCounts[connectionId] ?? 0,
    });

    // The exhaustion check runs HERE, not inside maybeRestartIce. Handing a spent budget to that
    // ladder writes the peer off on the spot and skips the rebuild rung entirely — and a rebuild
    // is exactly what a transport that never started needs.
    if ((this.iceRestartCounts[connectionId] ?? 0) >= this.maxIceRestarts) {
      this.rebuildPeerTransport(connectionId, 'stalled-transport');
      return;
    }

    this.maybeRestartIce(connectionId);
    // restartIce renegotiates through onnegotiationneeded, which never re-arms the deadline; without
    // this the ladder stops after one restart and never reaches the rebuild rung.
    this.armConvergenceReport(connectionId);
  }

  /**
   * AVS-4484: the actor for the dead-receiver watchdog (`utils/ui/voipStats.ts`) and for the 2 s check
   * after a WebKit rollback. WebKit never delivers media from a receiver that a remote offer created
   * after a rollback of our own offer: the tracks stay muted while frames decode and audio bytes
   * arrive. Only a fresh pc heals it.
   *
   * The replacement is offered at once, for every peer, initiator or not: `rebuildPeerTransport` offers
   * only when published or the lexical initiator, so a published receiver without a local stream would
   * wait out the 10 s waiting-offer watchdog. It spends its own per-peer budget, never
   * `transportRebuilds`, and at the cap it reports once and leaves the link (and its audio) alone
   * rather than writing the peer off.
   */
  public escalateDeadReceiver(connectionId: string, evidence: IDeadReceiverEvidence): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const pc = entry?.peerConnection;
    if (!entry || !pc) return;
    if (entry.transportFailed) return;
    if (pc.connectionState !== 'connected') return;
    if (pc.signalingState !== 'stable' || this.makingOffer[connectionId]) return;
    if (this.deadReceiverGiveUpSent[connectionId]) return;

    // A connected remote refuses an offer whose m-lines differ from its session (verified with the real
    // client in WebKit), so the fresh pc must repeat them. When the layout cannot be repeated the link
    // is left alone: a rebuild that the remote rejects would kill the audio that still works.
    const layout = mirrorableMlines(pc.currentRemoteDescription?.sdp);
    const spent = this.deadReceiverRebuilds[connectionId] ?? 0;
    if (!layout || spent >= this.maxDeadReceiverRebuilds) {
      this.deadReceiverGiveUpSent[connectionId] = true;
      reportRtc('dead-receiver-give-up', {
        connectionId,
        rebuilds: spent,
        reason: layout ? 'rebuilds-spent' : 'unmirrorable',
        ...evidence,
      });
      return;
    }

    const fresh = this.replacePeerConnection(connectionId, 'dead-receiver');
    if (!fresh) return;
    this.deadReceiverRebuilds[connectionId] = spent + 1;
    reportRtc('dead-receiver-escalation', { connectionId, attempt: spent + 1, ...evidence });

    // One recvonly transceiver per remote section, in order, BEFORE our media is attached: sendTrackToPeer
    // reuses a recvonly transceiver of the same kind, so the fresh offer carries the remote's m-line
    // count, order and mids.
    layout.forEach((kind) => fresh.addTransceiver(kind, { direction: 'recvonly' }));
    const placed = this.placeTracksOnMirroredSections(pc, fresh);

    // Hold the offer latch across attach and offer, as recoverFromRejectedOffer does: addTrack queues a
    // negotiationneeded task, and driveNegotiation must bail on the latch sendOffer keeps raised.
    this.makingOffer[connectionId] = true;
    this.attachLocalMedia(fresh, connectionId, this.receiveOnly, placed);
    this.sendOffer(fresh, connectionId).catch((error: unknown) => {
      log.log('[VOIP] Offer on the rebuilt peer connection failed for', connectionId, error);
    });
    this.armConvergenceReport(connectionId);
  }

  /**
   * Put each live local track of `oldPc` on the transceiver that holds the same mid in the mirrored
   * `fresh` pc. `attachLocalMedia` alone would give a track the first free section of its kind, which
   * moves it off the m-line the remote expects it on when an earlier section of that kind is not ours.
   * A receive-only client publishes nothing, so it places nothing.
   *
   * Returns the tracks it placed. `replaceTrack` updates `sender.track` in a queued task, so the
   * generic attach pass cannot tell a placed track from a free one by reading the sender: it skips
   * these by identity instead, or it would put the same track on a second section of its kind.
   */
  private placeTracksOnMirroredSections(oldPc: RTCPeerConnection, fresh: RTCPeerConnection): Set<MediaStreamTrack> {
    const placed = new Set<MediaStreamTrack>();
    if (this.receiveOnly) return placed;
    const stream = myCameraStream().stream;
    const mirrored = fresh.getTransceivers();
    for (const old of oldPc.getTransceivers()) {
      const track = old.sender.track;
      const target = old.mid === null ? undefined : mirrored[Number(old.mid)];
      if (!track || track.readyState === 'ended' || !target || target.receiver.track.kind !== track.kind) continue;
      void target.sender.replaceTrack(track);
      if (stream && typeof target.sender.setStreams === 'function') target.sender.setStreams(stream);
      target.direction = 'sendrecv';
      placed.add(track);
    }

    return placed;
  }

  /**
   * Start one 2 s check on the peer's current pc, with a same-pc baseline of the inbound counters so
   * the later read can prove GROWTH (non-zero but flat counters are history, not a dead receiver).
   */
  private startDeadReceiverCheck(connectionId: string): void {
    const peerConnection = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!peerConnection) return;

    const check: TDeadReceiverCheck = {
      peerConnection,
      timer: setTimeout(() => this.runDeadReceiverCheck(connectionId, check), this.deadReceiverCheckMs),
    };
    this.deadReceiverChecks[connectionId] = check;
    void this.readInboundCounters(peerConnection).then((counters) => {
      if (counters && this.deadReceiverChecks[connectionId] === check) check.baseline = counters;
    });
  }

  private runDeadReceiverCheck(connectionId: string, check: TDeadReceiverCheck): void {
    if (this.deadReceiverChecks[connectionId] !== check) return;
    delete this.deadReceiverChecks[connectionId];

    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc || pc !== check.peerConnection) {
      this.clearDeadReceiverCheck(connectionId);
      return;
    }
    // A rollback often queues a follow-up offer, so negotiation is usually still busy here. Wait for
    // the return to stable instead of retrying on a clock. No baseline yet is not zero growth: skip.
    if (pc.signalingState !== 'stable' || this.makingOffer[connectionId] || !check.baseline) {
      this.deadReceiverCheckPending[connectionId] = true;
      return;
    }
    void this.evaluateDeadReceiver(connectionId, check, check.baseline);
  }

  private async evaluateDeadReceiver(
    connectionId: string,
    check: TDeadReceiverCheck,
    baseline: TInboundCounters
  ): Promise<void> {
    const counters = await this.readInboundCounters(check.peerConnection);
    const live = this.store.get('remoteConnections')[connectionId];
    // The pc, the sender's mute flags and the tracks can all change while the read is in flight.
    if (!counters || !live || live.peerConnection !== check.peerConnection) return;

    delete this.deadReceiverCheckPending[connectionId];
    // Each muted track is judged by its own counter: a healthy receiver of the same kind next to it
    // (a second camera, a screen share) must not vouch for a muted one that is only idle.
    const rising = risingMutedKinds(mutedReceiverTrackIds(live.stream), baseline.byTrack, counters.byTrack);
    const video = rising.video && live.videoMuted !== true;
    const audio = rising.audio && live.audioMuted !== true;
    if (!video && !audio) return;

    this.escalateDeadReceiver(connectionId, {
      kinds: { audio, video },
      framesDecoded: counters.frames,
      audioBytes: counters.audioBytes,
    });
  }

  /**
   * The pc returned to stable while a check waited: run it again after the same 2 s grace (a follow-up
   * offer that adds a receiver leaves that track muted for a moment, and a rollback passes through a
   * transient stable). The flag stays set until a check has run with every gate open.
   */
  private rearmDeadReceiverCheck(connectionId: string): void {
    if (!this.deadReceiverCheckPending[connectionId] || this.deadReceiverChecks[connectionId]) return;
    const attempts = this.deadReceiverCheckAttempts[connectionId] ?? 0;
    if (attempts >= this.maxDeadReceiverCheckRearms) return;
    this.deadReceiverCheckAttempts[connectionId] = attempts + 1;
    this.startDeadReceiverCheck(connectionId);
  }

  /** Release the timer, the pending flag and the attempt counter of a peer's dead-receiver check. */
  private clearDeadReceiverCheck(connectionId: string): void {
    const check = this.deadReceiverChecks[connectionId];
    if (check) clearTimeout(check.timer);
    delete this.deadReceiverChecks[connectionId];
    delete this.deadReceiverCheckPending[connectionId];
    delete this.deadReceiverCheckAttempts[connectionId];
  }

  /** The inbound counters of one pc, or `null` when `getStats()` failed. Raw reads: this module keeps zero telemetry imports. */
  private async readInboundCounters(peerConnection: RTCPeerConnection): Promise<TInboundCounters | null> {
    let report: RTCStatsReport;
    try {
      report = await peerConnection.getStats();
    } catch {
      return null;
    }
    const entries: Record<string, unknown>[] = [];
    let audioBytes = 0;
    let frames = 0;
    report.forEach((stat: Record<string, unknown>) => {
      entries.push(stat);
      if (stat.type !== 'inbound-rtp') return;
      if (stat.kind === 'audio') audioBytes += Number(stat.bytesReceived) || 0;
      if (stat.kind === 'video') frames += Number(stat.framesDecoded) || 0;
    });
    return { audioBytes, frames, byTrack: inboundCountersByTrack(entries) };
  }

  /**
   * AVS-4441: the actor for the silent-transport watchdog in `utils/ui/voipStats.ts`.
   *
   * On prod a 3D stream delivered 10 frames and stopped: ICE 'connected', 54 ms RTT, zero loss,
   * zero NACKs. Every health signal this class watches stayed green, so no ladder ran. The AVS-4419
   * sampler was already measuring the pair every 5 s — it had nobody to tell. Public because that
   * sampler is the caller; the policy (how many windows, which rung, when to write off) stays here
   * with the rest of the recovery ladder, and the sampler stays a measurement.
   *
   * @param connectionId - the peer whose transport has carried nothing for a full window
   */
  public escalateSilentTransport(connectionId: string): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const pc = entry?.peerConnection;
    if (!entry || !pc) return;
    if (entry.transportFailed) return;
    // A peer that muted mic AND camera sends nothing by choice. The sampler already skips it, but
    // it reads the store before awaiting getStats, and a StreamUpdate can land in between.
    if (entry.audioMuted === true && entry.videoMuted === true) return;
    // Silence only means something on a transport that is actually up. A pair that has not
    // connected yet belongs to escalateStalledTransport; letting both ladders spend their budgets
    // on the same peer would double the ICE restarts it sees.
    if (pc.connectionState !== 'connected') return;

    const spent = this.silentTransportEscalations[connectionId] ?? 0;

    if (spent >= this.maxSilentTransportEscalations) {
      log.warn('[VOIP] Transport silent after every recovery attempt, writing peer off', {
        connectionId,
        escalations: spent,
      });
      reportRtc('silent-transport-give-up', {
        connectionId,
        escalations: spent,
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
      });
      this.markTransportFailed(connectionId);
      return;
    }

    this.silentTransportEscalations[connectionId] = spent + 1;
    log.log('[VOIP] Transport silent, restarting ICE', connectionId, spent + 1);
    reportRtc('silent-transport-escalation', {
      connectionId,
      attempt: spent + 1,
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
    });
    // Restarted directly, on this ladder's own budget — never through maybeRestartIce, for the same
    // reason as escalateStalledVideo (AVS-4458). A restart on a connected pair fires no fresh
    // 'connected' event, so the ICE budget it spent never came back. Two silent windows drained it,
    // and the next real ICE failure wrote the peer off without a single restart.
    recordVoipIceRestart();
    pc.restartIce();
  }

  /**
   * AVS-4458: the actor for the stalled-video watchdog in `utils/ui/voipStats.ts`.
   *
   * Prelive 2026-09-16: a pair's audio played for ten minutes while its video packets arrived and
   * decoded to nothing — gray tiles until a reload. The transport was up, so no ICE ladder ran, and
   * the flowing audio kept the silent-transport ladder quiet. Two ICE restarts, then the give-up the
   * product chose: keep the audio, and ask the SENDER to turn its camera off for everyone, exactly
   * as if its user pressed the button. Every peer then shows the avatar and the video-off icon
   * instead of a gray tile. This ladder never writes the peer off and never closes the pc — the
   * audio is the part that works.
   *
   * @param connectionId - the peer whose video packets arrive but never decode
   * @param evidence - the inbound video counters the watchdog read on its escalating tick
   */
  public escalateStalledVideo(connectionId: string, evidence: IStalledVideoEvidence): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const pc = entry?.peerConnection;
    if (!entry || !pc) return;
    if (entry.transportFailed) return;
    // The sampler skips a camera-off peer, but it reads the store before awaiting getStats, and a
    // StreamUpdate can land in between.
    if (entry.videoMuted === true) return;
    // A pair that is not connected belongs to the transport ladders; spending this budget as well
    // would double the ICE restarts it sees.
    if (pc.connectionState !== 'connected') return;
    // An offer is in flight (AVS-4442 glare) — restarting ICE now fights it. The same pair of
    // checks as driveNegotiation. The next window tries again; no budget is spent.
    if (pc.signalingState !== 'stable' || this.makingOffer[connectionId]) return;
    // The request went out once. A sender on a stale bundle ignores it, and that must not turn
    // into one alert per window for the rest of the call.
    if (this.stalledVideoGiveUpSent[connectionId]) return;

    const spent = this.stalledVideoEscalations[connectionId] ?? 0;

    if (spent >= this.maxStalledVideoEscalations) {
      this.stalledVideoGiveUpSent[connectionId] = true;
      log.warn('[VOIP] Video still not decoding after every recovery attempt, asking the peer to turn its camera off', {
        connectionId,
        escalations: spent,
      });
      reportRtc('stalled-video-give-up', { connectionId, escalations: spent, ...evidence, hasStream: !!entry.stream });
      this.signalerClient.signal({
        action: ERTCEvents.StreamUpdate,
        to: [connectionId],
        data: { videoOffRequest: true },
      });
      return;
    }

    this.stalledVideoEscalations[connectionId] = spent + 1;
    log.log('[VOIP] Video not decoding, restarting ICE', connectionId, spent + 1);
    reportRtc('stalled-video-escalation', {
      connectionId,
      attempt: spent + 1,
      ...evidence,
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
    });
    this.setVideoStalled(connectionId, true);
    // Restarted directly, on this ladder's own budget — never through maybeRestartIce. That budget
    // is for ICE failures and refills only on a fresh 'connected' event, which an ICE restart on a
    // healthy transport never fires (verified in the browser, AVS-4458). Spending it here drained it
    // for good: the next stall skipped its restarts, and a later real ICE failure found the budget
    // spent and wrote the whole peer off, audio included.
    recordVoipIceRestart();
    pc.restartIce();
  }

  /**
   * AVS-4458: the stalled-video watchdog saw frames decode again after an escalation, so the ladder
   * gets its budget back. Silent when this peer holds no stalled-video state: a takeover's rebuilt
   * client never escalated it, and a recovery it never saw is not one to report.
   *
   * @param connectionId - the peer whose video decodes again
   */
  public clearStalledVideo(connectionId: string): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const spent = this.stalledVideoEscalations[connectionId];
    if (spent === undefined && !this.stalledVideoGiveUpSent[connectionId] && !entry?.videoStalled) return;
    delete this.stalledVideoEscalations[connectionId];
    delete this.stalledVideoGiveUpSent[connectionId];
    this.setVideoStalled(connectionId, false);
    log.log('[VOIP] Video decoding again', connectionId);
    reportRtc('stalled-video-recovered', { connectionId, escalations: spent ?? 0 });
  }

  /** AVS-4458: writes the local `videoStalled` observation. No-ops when unchanged so a healthy call never ticks the store. */
  private setVideoStalled(connectionId: string, videoStalled: boolean): void {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[connectionId];
    if (!connection || !!connection.videoStalled === videoStalled) return;
    this.store.setValues({
      remoteConnections: { ...remoteConnections, [connectionId]: { ...connection, videoStalled } },
    });
  }

  /**
   * AVS-4458: a receiver gave up on our video — it arrives there but never decodes — and asks us
   * to turn the camera off. Honoured through toggleMedia, the camera button's own path: every peer
   * is told `videoMuted:true` and shows the avatar, and the local control shows the camera off.
   * Only a tab that publishes a stream with its camera on has anything to turn off, and each peer
   * is honoured once per local unmute.
   *
   * @param from - the receiver that sent the request
   */
  private handleVideoOffRequest(from: string): void {
    if (!this.published || this.receiveOnly) return;
    if (!this.store.get('me').stream) return;
    if (!myCameraStream().get('isVideoEnabled')) return;
    if (this.videoOffHonored[from]) return;
    this.videoOffHonored[from] = true;
    log.warn('[VOIP] A peer cannot decode our video, turning the camera off', from);
    reportRtc('video-off-requested', { connectionId: from });
    this.toggleMedia('video');
  }

  private async offerIfNeverNegotiated(connectionId: string): Promise<void> {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    // Fire-time re-check: a set remoteDescription means the initiator delivered, and offering
    // from a non-stable state would throw — both bow out. Glare stays safe when we DO offer:
    // we are the polite (lower-cid) peer, so a late-colliding initiator offer wins via rollback.
    if (!pc || pc.remoteDescription !== null || pc.signalingState !== 'stable') return;
    log.log(
      '[VOIP] Initiator offer never arrived — offering from the waiting side',
      connectionId,
      Date.now().toString().slice(-5)
    );
    await this.forceReoffer(connectionId);
  }

  /**
   * AVS-4302: the onnegotiationneeded happy path. Drives a fresh offer ONLY from a clean stable
   * state (a track added on this side, a direction change, or restartIce()). The makingOffer guard
   * keeps it to one offer at a time. Recovery from a NON-stable state is forceReoffer's job — this
   * deliberately bails when not stable so the two never collide.
   */
  private async driveNegotiation(connectionId: string): Promise<void> {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc || pc.signalingState !== 'stable' || this.makingOffer[connectionId]) return;
    await this.sendOffer(pc, connectionId);
  }

  /**
   * AVS-4302: recovery re-offer for a peer stuck awaiting an answer. Legal from stable OR
   * have-local-offer (createOffer supersedes the pending offer). It NEVER rolls back — so a slow
   * answer to the prior offer cannot corrupt a survivor (review gate C1). It fires on whoever is
   * stranded, independent of the peer's state (review gate C2), and is bounded by
   * maxRenegotiationRetries. sendOffer re-arms the timer, so a still-unanswered re-offer retries
   * until the cap.
   */
  private async forceReoffer(connectionId: string): Promise<void> {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc || pc.signalingState === 'closed' || pc.connectionState === 'closed') return;
    if (this.makingOffer[connectionId]) return;
    if ((this.renegotiationRetries[connectionId] ?? 0) >= this.maxRenegotiationRetries) {
      log.warn('[VOIP] Giving up renegotiation after retries', connectionId, Date.now().toString().slice(-5));
      return;
    }
    this.renegotiationRetries[connectionId] = (this.renegotiationRetries[connectionId] ?? 0) + 1;
    // The old dropped offer's ICE is moot now that we are superseding it.
    this.ignoringOffer[connectionId] = false;
    await this.sendOffer(pc, connectionId);
  }

  // AVS-4302: the single stable-reaching hook (wired to onsignalingstatechange). Every path to
  // stable — our offer answered, or we sent an answer — clears the recovery timer here, so disarm
  // coverage cannot be missed by an individual handler.
  private handleSignalingStateChange(connectionId: string): void {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc) return;
    if (pc.signalingState === 'stable') {
      this.clearRenegotiationTimer(connectionId);
      this.rearmDeadReceiverCheck(connectionId);
    }
  }

  /**
   * AVS-4302: ICE is ground truth for "media actually flows" — signalingState is not. A pair that
   * negotiated SDP but whose transport fails/disconnects is recovered with restartIce (→
   * onnegotiationneeded → a fresh offer carrying new ICE). 'disconnected' gets one grace window
   * before escalating (it is often transient); 'failed' escalates at once. Bounded by maxIceRestarts
   * so a permanently dead peer cannot drive unbounded churn. Inert on a closed pc.
   */
  private handleIceConnectionStateChange(connectionId: string): void {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc || pc.connectionState === 'closed') return;
    const state = pc.iceConnectionState;
    if (state === 'connected' || state === 'completed') {
      this.clearIceGraceTimer(connectionId);
      // AVS-4392 B2: recovery SUCCEEDED — hand both ladders their full budget back. They were
      // previously reset only on session reset, reconnect or peer removal, so the counts only ever
      // ratcheted upward across a call: a long session accumulated unrelated transient blips until
      // the budget was spent, and the next blip was permanently fatal with no recovery attempted.
      delete this.iceRestartCounts[connectionId];
      delete this.iceGiveUpReported[connectionId];
      delete this.transportRebuilds[connectionId];
      // AVS-4392 B3: only clear the give-up flag when the AGGREGATE transport is healthy again.
      // ICE can flap back to 'connected' on a pc whose DTLS is dead — that exact asymmetry is why
      // rebuildPeerTransport exists — and clearing here on such a pc would un-hide a tile that can
      // never carry media, with no further connectionState event left to re-hide or rebuild it.
      // This guard is also what makes the write-off sticky: a peer is only flagged while its
      // connectionState is 'failed', DTLS failure is terminal, and the flag blocks the rebuild that
      // would swap the pc — so the flag outlives every ICE flap and only a fresh transport clears it.
      if (pc.connectionState !== 'failed') this.clearTransportFailed(connectionId);
      return;
    }
    if (state === 'failed') {
      this.clearIceGraceTimer(connectionId);
      this.maybeRestartIce(connectionId);
      return;
    }
    if (state === 'disconnected') {
      if (this.iceGraceTimers[connectionId]) return;
      this.iceGraceTimers[connectionId] = setTimeout(() => {
        delete this.iceGraceTimers[connectionId];
        const current = this.store.get('remoteConnections')[connectionId]?.peerConnection;
        if (current && current.iceConnectionState === 'disconnected') this.maybeRestartIce(connectionId);
      }, this.iceDisconnectGraceMs);
    }
  }

  /**
   * AVS-4311 follow-up (gray-tile bug): `connectionState` aggregates ICE AND DTLS transport
   * states. A DTLS failure surfaces as connectionState 'failed' while iceConnectionState stays
   * 'connected' — a state the ICE-watching recovery (handleIceConnectionStateChange) never sees,
   * and one restartIce() cannot fix (the DTLS association is dead; verified live). Without this
   * hook such a pair stayed signaling-stable with muted receiver tracks forever: the remote
   * rendered as a permanent gray tile while its mute badges (signaling channel) kept working.
   *
   * 'closed' keeps the pre-existing remove behaviour. An ICE-level 'failed' is left to the
   * ICE-restart path — rebuilding there would race maybeRestartIce over the same pc.
   */
  private handleConnectionStateChange(connectionId: string): void {
    const peerConnection = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!peerConnection) return;
    if (peerConnection.iceConnectionState === 'closed') {
      log.log('[VOIP] Peer Connection Closed - ', connectionId, Date.now().toString().slice(-5));
      this.removePeer(connectionId);
      peerConnection.close();
      return;
    }
    if (peerConnection.connectionState === 'failed' && peerConnection.iceConnectionState !== 'failed') {
      this.rebuildPeerTransport(connectionId, 'dtls-failed');
    }
  }

  /**
   * Replace a DTLS-dead RTCPeerConnection with a fresh one on the SAME store entry and
   * re-negotiate. Both sides of the pair observe connectionState 'failed', so both rebuild:
   * the lexical initiator re-offers explicitly (prepareConnectionAndOffer), the other side's
   * track re-add fires onnegotiationneeded → driveNegotiation — glare-safe under AVS-4302
   * perfect negotiation. Bounded by maxTransportRebuilds (see replacePeerConnection).
   */
  private rebuildPeerTransport(connectionId: string, reason: TTransportRebuildReason): void {
    if (!this.replacePeerConnection(connectionId, reason)) return;
    if (this.published) {
      void this.prepareConnectionAndOffer(connectionId, this.receiveOnly);
    }
  }

  /**
   * Swap a peer's RTCPeerConnection for a fresh one on the SAME store entry — the rung shared by
   * rebuildPeerTransport (which then re-negotiates) and a rejected inbound offer (AVS-4465, which
   * answers that offer on the fresh pc). Owns the give-up latch, the maxTransportRebuilds budget and
   * the teardown, so every caller spends the same budget. Only the transport ladders reach the
   * give-up: a rejected offer never calls this with the budget spent. Returns the fresh pc, or
   * `undefined` when the peer is written off, the budget is spent, or this browser builds no pc.
   */
  private replacePeerConnection(connectionId: string, reason: TTransportRebuildReason): RTCPeerConnection | undefined {
    const oldPeerConnection = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!oldPeerConnection) return undefined;
    // AVS-4392 B3: the give-up FLAG is the latch here, not the budget — handleIceConnectionStateChange
    // returns the budget unconditionally, so an ICE flap on a DTLS-dead corpse refills it and the next
    // connectionState event would rebuild (and, via createPeerConnection, un-hide) a written-off peer.
    if (this.store.get('remoteConnections')[connectionId]?.transportFailed) return undefined;
    // AVS-4484: the collision and dead-receiver replacements keep off the shared budget, and build the
    // replacement BEFORE the old pc is touched: a browser that refuses to construct one must leave the
    // caller's fallback a live pc with its handlers, not a closed one. Every path after `prebuilt` is
    // built must reach `createPeerConnection`, or the pc goes unreported and unclosed.
    const exempt = BUDGET_EXEMPT_REBUILD_REASONS.has(reason);
    let prebuilt: RTCPeerConnection | undefined;
    if (exempt) {
      prebuilt = this.constructPeerConnection(connectionId);
      if (!prebuilt) return undefined;
    }
    if (!exempt && (this.transportRebuilds[connectionId] ?? 0) >= this.maxTransportRebuilds) {
      // AVS-4392 B3: the give-up used to just log and return, leaving a peer that keeps its tile
      // and keeps answering SDP — a permanent gray rectangle with live mute badges. An already
      // flagged peer turns back at the latch above, so this records the death exactly once.
      log.warn('[VOIP] Giving up transport rebuild after retries', connectionId, Date.now().toString().slice(-5));
      reportRtc('transport-rebuild-give-up', {
        connectionId,
        reason,
        rebuilds: this.transportRebuilds[connectionId] ?? 0,
        maxTransportRebuilds: this.maxTransportRebuilds,
        connectionState: oldPeerConnection.connectionState,
        iceConnectionState: oldPeerConnection.iceConnectionState,
      });
      this.markTransportFailed(connectionId);
      return undefined;
    }
    if (!exempt) {
      this.transportRebuilds[connectionId] = (this.transportRebuilds[connectionId] ?? 0) + 1;
      recordVoipTransportRebuild();
    }
    log.log('[VOIP] Rebuilding peer connection', connectionId, reason, Date.now().toString().slice(-5));
    reportRtc('transport-rebuild', {
      connectionId,
      reason,
      ...(exempt
        ? {}
        : { attempt: this.transportRebuilds[connectionId], maxTransportRebuilds: this.maxTransportRebuilds }),
      connectionState: oldPeerConnection.connectionState,
      iceConnectionState: oldPeerConnection.iceConnectionState,
    });

    // Detach the old pc's handlers BEFORE closing (mirrors destroy()): a close-induced event on
    // the dying pc would re-enter handleConnectionStateChange while the store still points at it
    // and removePeer() the very entry (connectionId, meta) the rebuild reuses.
    oldPeerConnection.onnegotiationneeded = null;
    oldPeerConnection.onsignalingstatechange = null;
    oldPeerConnection.oniceconnectionstatechange = null;
    oldPeerConnection.onicecandidate = null;
    oldPeerConnection.ontrack = null;
    oldPeerConnection.onconnectionstatechange = null;
    oldPeerConnection.close();
    // Reset per-peer negotiation state so the fresh transport starts clean (mirrors removePeer):
    // stale glare flags would misclassify the first post-rebuild offer, and the old transport's
    // timers/budgets belong to the dead pc.
    delete this.makingOffer[connectionId];
    delete this.ignoringOffer[connectionId];
    this.clearRenegotiationTimer(connectionId);
    this.clearIceGraceTimer(connectionId);
    this.clearConvergenceTimer(connectionId);
    delete this.iceRestartCounts[connectionId];
    delete this.iceGiveUpReported[connectionId];
    delete this.silentTransportEscalations[connectionId];
    delete this.stalledVideoEscalations[connectionId];
    delete this.stalledVideoGiveUpSent[connectionId];
    delete this.videoOffHonored[connectionId];
    delete this.pendingCandidates[connectionId];
    this.clearDeadReceiverCheck(connectionId);

    return this.createPeerConnection(connectionId, prebuilt);
  }

  /**
   * AVS-4392 B3: mark a peer the recovery ladder has abandoned. The store entry survives — its
   * mute badges ride the signaling channel and still work — but `filterDeadTransportTiles`
   * (utils/ui/voipRemotes) drops its tile and `maybeRestartIce` stops churning it. The customer is
   * told through the existing `peer:disconnected` toast rather than watching a participant vanish
   * for no visible reason; identity-pending peers are suppressed exactly as ConnectionRemoved does.
   */
  private markTransportFailed(connectionId: string): void {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[connectionId];
    if (!connection || connection.transportFailed) return;
    recordVoipPeerWrittenOff();
    // AVS-4430 media-health: `transportFailed` is one of the three free primary signals (with
    // `stream == null` and the framesDecoded delta) and, unlike a gray tile, it names the peer the
    // recovery ladder wrote off. Reported at the flag's single writer, so it cannot double-fire.
    reportRtc('transport-failed', {
      connectionId,
      hasStream: !!connection.stream,
      metaPending: !!connection.metaPending,
      connectionState: connection.peerConnection?.connectionState ?? '',
      iceConnectionState: connection.peerConnection?.iceConnectionState ?? '',
    });
    this.store.setValues({
      remoteConnections: { ...remoteConnections, [connectionId]: { ...connection, transportFailed: true } },
    });
    if (!connection.metaPending) {
      this.emit('peer:disconnected', connectionId, this.store.get('remoteConnections'), connection.meta);
    }
  }

  /** Clears the give-up flag. No-ops when unset so a healthy call never ticks the store. */
  private clearTransportFailed(connectionId: string): void {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[connectionId];
    if (!connection?.transportFailed) return;
    this.store.setValues({
      remoteConnections: { ...remoteConnections, [connectionId]: { ...connection, transportFailed: false } },
    });
  }

  private maybeRestartIce(connectionId: string): void {
    const connection = this.store.get('remoteConnections')[connectionId];
    const pc = connection?.peerConnection;
    if (!pc || pc.connectionState === 'closed') return;
    // AVS-4392 B3: the rebuild ladder already gave up on this peer. Every rebuild deletes
    // iceRestartCounts (below), so this ladder was handed a full budget again by the very rebuild
    // that exhausted the other one — the two then churned a corpse for minutes, visible in the
    // field log as "Giving up transport rebuild" followed by "Restarting ICE" five seconds later.
    if (connection?.transportFailed) return;
    if ((this.iceRestartCounts[connectionId] ?? 0) >= this.maxIceRestarts) {
      // AVS-4430: this used to be a BARE `return` — no log, no counter, no event. It is the exact
      // path a VPN drop takes, the dominant failure mode, and it left zero trace even with a fully
      // debug console; the DTLS ladder seventy lines above already announces its own give-up.
      // Latched so an exhausted peer that keeps flapping ICE reports once, not once per event.
      if (!this.iceGiveUpReported[connectionId]) {
        this.iceGiveUpReported[connectionId] = true;
        // AVS-4419: the recorder event below exists only on dev/prelive builds. On prod this warn
        // and the session counter are the whole trace — the same shape as the DTLS ladder's give-up.
        log.warn('[VOIP] Giving up ICE restart after retries', connectionId, Date.now().toString().slice(-5));
        recordVoipIceRestartGiveUp();
        reportRtc('ice-restart-give-up', {
          connectionId,
          iceRestarts: this.iceRestartCounts[connectionId] ?? 0,
          maxIceRestarts: this.maxIceRestarts,
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState,
          signalingState: pc.signalingState,
          hasStream: !!connection?.stream,
          transportFailed: !!connection?.transportFailed,
        });
      }
      // Write the peer off — an exhausted ICE ladder is the dominant network-drop path, and until
      // now only the DTLS ladder ever set the flag `filterDeadTransportTiles` reads (AVS-4420).
      // Deliberately outside the reporting latch above: the write-off must not depend on whether
      // the warn fired, and `markTransportFailed` is idempotent. Locally one-way — the tile returns
      // when the remote restarts ICE or signaling replaces the peer, not on its own.
      this.markTransportFailed(connectionId);
      return;
    }
    this.iceRestartCounts[connectionId] = (this.iceRestartCounts[connectionId] ?? 0) + 1;
    recordVoipIceRestart();
    log.log('[VOIP] Restarting ICE', connectionId, Date.now().toString().slice(-5));
    reportRtc('ice-restart', {
      connectionId,
      attempt: this.iceRestartCounts[connectionId],
      maxIceRestarts: this.maxIceRestarts,
      iceConnectionState: pc.iceConnectionState,
      connectionState: pc.connectionState,
    });
    pc.restartIce();
  }

  private clearIceGraceTimer(connectionId: string): void {
    if (this.iceGraceTimers[connectionId]) {
      clearTimeout(this.iceGraceTimers[connectionId]);
      delete this.iceGraceTimers[connectionId];
    }
  }

  addMyTrackToAllPeers(track: MediaStreamTrack) {
    const remoteConnections = this.store.get('remoteConnections');
    Object.values(remoteConnections).forEach(({ peerConnection, connectionId }) => {
      if (peerConnection) {
        const needsOffer = this.sendTrackToPeer(track, peerConnection);
        if (needsOffer) {
          this.sendOffer(peerConnection, connectionId);
        }
      }
    });
  }

  removeMyTrackFromAllPeers(track: MediaStreamTrack) {
    const remoteConnections = this.store.get('remoteConnections');
    Object.values(remoteConnections).forEach(({ connectionId, peerConnection }) => {
      if (!peerConnection) {
        return;
      }
      const sender = peerConnection.getSenders()?.find((sender) => sender.track === track);

      if (sender) {
        // Use replaceTrack(null) instead of removeTrack to keep the sender/transceiver
        // reusable. This avoids orphan sender accumulation and SDP growth on repeated
        // mute/unmute cycles. Muted state is signaled separately via StreamUpdate.
        log.log('[VOIP] Muting sender for', track.kind, 'on', connectionId, Date.now().toString().slice(-5));
        sender.replaceTrack(null).catch((err: unknown) => {
          log.log('[VOIP] replaceTrack(null) failed for', connectionId, err, Date.now().toString().slice(-5));
        });
      }
    });
  }

  private async handleOfferSignals(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      console.warn('[VOIP] Peer Connection not found for offer from', from);
      return;
    }

    const peerConnection = connection.peerConnection;
    const signalingState = peerConnection.signalingState;

    // AVS-4302 — perfect-negotiation glare resolution. Two publishers (two dealer masters) can
    // fire offers simultaneously; the old code only handled 'stable' and silently dropped a
    // colliding offer received in have-local-offer, deadlocking the master↔master pair.
    //
    // have-remote-offer: we are already applying/answering a prior offer. A second one is a race
    // artifact — setRemoteDescription(offer) does NOT implicit-rollback cleanly from this state —
    // so drop it; the peer re-offers from stable if it still needs to.
    if (signalingState === 'have-remote-offer') {
      log.log('[VOIP] Ignoring offer while answering a prior one (have-remote-offer)', from);
      return;
    }

    // Empty cid (pre-connect / mid-reconnect window): '' < anything would make BOTH peers
    // classify as polite (double rollback → dropped answers → deadlock), so never role-classify
    // here. Answer only from a clean stable state; otherwise drop and let the peer re-offer.
    const myConnectionId = this.store.get('me').connectionId;
    if (!myConnectionId) {
      if (signalingState === 'stable') {
        await this.answerOffer(peerConnection, data as RTCSessionDescriptionInit, from!);
      } else {
        log.log('[VOIP] Offer received with unknown local cid in non-stable state; ignoring', from);
      }
      return;
    }

    // Only have-local-offer (or a still-in-flight local offer) is a rollback-able collision. The
    // impolite peer (higher cid — the same one getInitiator picks) keeps its own offer and drops
    // the inbound one; the polite peer rolls back and answers.
    const polite = this.isPolite(myConnectionId, from!);
    const rollbackCollision = this.makingOffer[from!] || signalingState === 'have-local-offer';
    if (!polite && rollbackCollision) {
      log.log('[VOIP] Ignoring colliding offer (impolite peer keeps its own)', from);
      this.ignoringOffer[from!] = true;
      return;
    }

    await this.answerOffer(peerConnection, data as RTCSessionDescriptionInit, from!);
  }

  /**
   * AVS-4302: accept an offer and reply with an answer. `setRemoteDescription(offer)` performs
   * an IMPLICIT ROLLBACK of our own pending offer when called in have-local-offer (the polite
   * path); from stable it is a plain apply. Rollback discards only the pending local SDP, NOT our
   * RTCRtpSenders/transceivers — so a publishing master's camera senders survive and createAnswer
   * re-advertises them sendrecv (do NOT re-add tracks here; that would double-add).
   */
  private async answerOffer(
    peerConnection: RTCPeerConnection,
    offer: RTCSessionDescriptionInit,
    from: string
  ): Promise<void> {
    log.log('[VOIP] Accepting offer from ', from, peerConnection.signalingState, Date.now().toString().slice(-5));

    // AVS-4465: an offer of ours was pending — rolled back implicitly (have-local-offer), or still
    // being created (makingOffer) when this one won the collision.
    const hadOwnOffer = peerConnection.signalingState === 'have-local-offer' || !!this.makingOffer[from];
    // AVS-4484: WebKit never delivers media from a receiver created by an offer applied after a rollback
    // of our own pending offer (a pending setLocalDescription still reads 'stable', but its queued apply
    // rolls back too). On a pc that never completed a negotiation, a fresh pc answers instead. Renegotiation
    // glare and Chromium keep the rollback below.
    if (
      hadOwnOffer &&
      peerConnection.currentRemoteDescription === null &&
      isWebKitEngine() &&
      (await this.answerCollisionOnFreshConnection(offer, from))
    ) {
      return;
    }
    const rejection = await rejectionOf(() => peerConnection.setRemoteDescription(offer));
    if (rejection) {
      await this.recoverFromRejectedOffer(peerConnection, offer, from, rejection);
      return;
    }
    if (hadOwnOffer) {
      this.stopOrphanedTransceivers(peerConnection, from);
      // AVS-4484: a rollback-and-apply on WebKit may have left the new receivers dead; check in 2 s.
      if (isWebKitEngine()) {
        this.clearDeadReceiverCheck(from);
        this.startDeadReceiverCheck(from);
      }
    }
    await this.answerAcceptedOffer(peerConnection, offer, from);
  }

  /**
   * AVS-4465: stop the recvonly transceivers our discarded offer added.
   *
   * A remote offer only adopts transceivers created by addTrack (JSEP 5.10; Chromium's
   * FindAvailableTransceiverToReceive). The recvonly ones from ensureReceiveTransceivers therefore
   * survive a polite rollback unassociated (mid null) while the remote offer brings transceivers of
   * its own — left alone they come back in our next offer as extra m-lines with fresh mids, the
   * churn this ticket is about. Stopping them still sets negotiation-needed once (a stopping
   * transceiver counts), but that follow-up offer keeps the m-lines we already have: measured in
   * Chromium 151 and WebKit 26.5, the next offer has 2 m-lines with the stop and 4 without it.
   * So the ticket's "no second offer" holds for the churn it describes: the follow-up adds no mids.
   * Only recvonly ones are stopped. A transceiver that carries our track still needs its own
   * renegotiation, and a muted one (sendrecv, track nulled by removeMyTrackFromAllPeers) must stay
   * reusable for replaceTrack on unmute.
   */
  private stopOrphanedTransceivers(peerConnection: RTCPeerConnection, connectionId: string): void {
    peerConnection.getTransceivers().forEach((transceiver) => {
      if (transceiver.direction !== 'recvonly' || transceiver.mid !== null || transceiver.sender.track !== null) return;
      log.log(
        '[VOIP] Stopping a recvonly transceiver the rollback left unassociated',
        connectionId,
        transceiver.receiver.track?.kind,
        Date.now().toString().slice(-5)
      );
      transceiver.stop();
    });
  }

  /**
   * AVS-4465: recover from a remote offer `peerConnection` refused.
   *
   * In session 3551deda the standby had applied a stale offer, so every later offer from that
   * customer failed with "order of m-lines". The rejection escaped as an unhandled rejection and the
   * pair waited ~17 s for ICE to fail before anything rebuilt it. Now the rejection is always
   * reported, and a pair that never connected replaces its pc at once and answers the SAME offer on
   * the fresh one: a fresh pc has no earlier negotiation to conflict with.
   *
   * A connected pair keeps its live media on its last good negotiation, exactly as before — a
   * fresh pc would cost a DTLS re-handshake and a media gap over one bad offer. A rejected ANSWER
   * never comes here: handleAnswerSignals keeps its re-offer timer, and the re-offer lands here at
   * the peer.
   */
  private async recoverFromRejectedOffer(
    peerConnection: RTCPeerConnection,
    offer: RTCSessionDescriptionInit,
    from: string,
    error: Error
  ): Promise<void> {
    log.warn('[VOIP] Remote offer rejected', from, peerConnection.connectionState, error);
    reportRtc('sdp-rejected', {
      connectionId: from,
      direction: 'remote',
      type: 'offer',
      reason: String(error),
      connectionState: peerConnection.connectionState,
    });

    // Only the pc that refused, and only while it is still ours: one closed by removePeer or
    // already swapped by a concurrent rebuild must not spend the shared rebuild budget.
    const current = this.store.get('remoteConnections')[from]?.peerConnection;
    if (current !== peerConnection) return;
    if (peerConnection.signalingState === 'closed' || peerConnection.connectionState === 'closed') return;
    if (peerConnection.connectionState === 'connected') return;
    // The rebuild budget is for dead transports. Once it is spent, a rejected offer leaves this pc
    // to the ICE and DTLS ladders, which write the peer off only if its transport really dies.
    if ((this.transportRebuilds[from] ?? 0) >= this.maxTransportRebuilds) return;

    // No await between the rejection and this call. A failed implicit rollback leaves the old pc
    // 'stable' with a negotiationneeded task queued; replacing it now nulls its handlers before that
    // task runs, so the discarded pc can never send an offer.
    const fresh = this.replacePeerConnection(from, 'offer-rejected');
    if (!fresh) return;

    await this.answerOnFreshConnection(fresh, offer, from);
  }

  /**
   * AVS-4484: answer a first-negotiation collision on a fresh pc instead of rolling back.
   *
   * Capped per peer (`maxCollisionReplacements`) and kept off the shared transport budget, so an offer
   * storm cannot churn pcs without bound and a real DTLS failure later still has its rebuilds.
   *
   * @returns {Promise<boolean>} true when the offer was answered on the replacement; false when the
   * caller must fall back to the rollback path (cap reached, peer written off, browser refused a pc)
   */
  private async answerCollisionOnFreshConnection(offer: RTCSessionDescriptionInit, from: string): Promise<boolean> {
    const spent = this.collisionReplacements[from] ?? 0;
    if (spent >= this.maxCollisionReplacements) {
      if (!this.collisionExhaustedReported[from]) {
        this.collisionExhaustedReported[from] = true;
        reportRtc('offer-collision-exhausted', { connectionId: from, replacements: spent });
      }
      return false;
    }
    // No await between the decision and the replacement, so the old pc's handlers are gone before any
    // of its queued tasks (negotiationneeded) can run.
    const fresh = this.replacePeerConnection(from, 'offer-collision');
    if (!fresh) return false;
    this.collisionReplacements[from] = spent + 1;
    await this.answerOnFreshConnection(fresh, offer, from);
    return true;
  }

  /**
   * Answer `offer` on `fresh`, a pc that just replaced the peer's old one: re-attach our media, apply the
   * offer, answer it, and restart ICE (the fresh pc never saw the candidates the peer trickled for it).
   * Shared by the rejected-offer recovery (AVS-4465) and the WebKit first-negotiation collision (AVS-4484).
   */
  private async answerOnFreshConnection(
    fresh: RTCPeerConnection,
    offer: RTCSessionDescriptionInit,
    from: string
  ): Promise<void> {
    // Hold the offer latch across attach and apply, as sendOffer does: addTrack queues a
    // negotiationneeded task while the fresh pc is still 'stable', and driveNegotiation must not
    // start an offer in the middle of this setRemoteDescription.
    this.makingOffer[from] = true;
    let secondRejection: Error | null;
    try {
      this.attachLocalMedia(fresh, from, this.receiveOnly);
      secondRejection = await rejectionOf(() => fresh.setRemoteDescription(offer));
    } finally {
      // A rebuild or removePeer during the await already reset the latch for whatever pc is there now.
      if (this.store.get('remoteConnections')[from]?.peerConnection === fresh) this.makingOffer[from] = false;
    }
    if (secondRejection) {
      // A fresh pc has nothing to conflict with, so the offer itself is bad. Another rebuild would
      // only spend the budget; the peer's own re-offer timer moves the pair on.
      log.warn('[VOIP] Remote offer rejected by a fresh peer connection too; not retrying', from, secondRejection);
      reportRtc('sdp-rejected', {
        connectionId: from,
        direction: 'remote',
        type: 'offer',
        reason: String(secondRejection),
        connectionState: fresh.connectionState,
        afterRebuild: true,
      });
      return;
    }
    const answerFailure = await rejectionOf(() => this.answerAcceptedOffer(fresh, offer, from));
    if (answerFailure) {
      log.warn('[VOIP] Answer on the fresh peer connection failed', from, answerFailure);
      reportRtc('sdp-rejected', {
        connectionId: from,
        direction: 'local',
        type: 'answer',
        reason: String(answerFailure),
        connectionState: fresh.connectionState,
        afterRebuild: true,
      });
    }

    if (this.store.get('remoteConnections')[from]?.peerConnection !== fresh) return;
    // The fresh pc never saw the candidates the peer trickled for this offer (the old pc took them,
    // and the rebuild dropped its queue). Peer-reflexive discovery alone fails through TURN, so an
    // ICE restart runs the whole candidate exchange again, one round trip later. Without an answer
    // there is no negotiation to restart.
    if (!answerFailure) this.maybeRestartIce(from);
    // replacePeerConnection cleared the deadline, and prepareConnectionAndOffer, which arms it, does not run here.
    // Armed even after a failed answer, so a stranded fresh pc still shows up as convergence-failed.
    this.armConvergenceReport(from);
  }

  /** Answer an offer that is now the pc's remote description, and send the answer. */
  private async answerAcceptedOffer(
    peerConnection: RTCPeerConnection,
    offer: RTCSessionDescriptionInit,
    from: string
  ): Promise<void> {
    reportRtc('sdp', { connectionId: from, direction: 'remote', description: offer });
    // We accepted an offer — the surviving negotiation is this one; stop swallowing its ICE.
    this.ignoringOffer[from] = false;

    log.log('[VOIP] Remote Description is SET for', from, Date.now().toString().slice(-5));
    const answer = await peerConnection.createAnswer();

    log.log('[VOIP] Answer created', from, answer, Date.now().toString().slice(-5));

    await peerConnection.setLocalDescription(answer);
    reportRtc('sdp', { connectionId: from, direction: 'local', description: answer });

    log.log('[VOIP] Local Description is SET for', from, answer, Date.now().toString().slice(-5));

    this.signalDescription(from, answer);

    log.log('[VOIP] Answer sent to', from, Date.now().toString().slice(-5));
  }

  private shareMyStream(): void {
    log.log('[VOIP] Sharing my stream', Date.now().toString().slice(-5));
    const { me, remoteConnections } = this.store.data;

    Object.values(remoteConnections).forEach((remoteConnection) => {
      if (me.stream) {
        me.stream.getTracks().forEach((track: MediaStreamTrack) => {
          log.log(
            '[VOIP] Sharing my stream track with ',
            remoteConnection.connectionId,
            Date.now().toString().slice(-5)
          );

          const senders = remoteConnection.peerConnection?.getSenders();
          const senderExists = senders?.some((sender) => sender.track === track);
          if (!senderExists) {
            this.prepareConnectionAndOffer(remoteConnection.connectionId);
          } else {
            log.log(
              '[VOIP] Sender already exists for ',
              remoteConnection.connectionId,
              Date.now().toString().slice(-5)
            );
          }
        });
      }
    });

    this.registerMyVideoStreamListeners();
  }

  /**
   * `new RTCPeerConnection`, and nothing else, inside the try.
   *
   * Reported once per client rather than once per peer: the refusal is a property of the browser,
   * not of the peer being admitted, and the offer burst in a multi-party call would otherwise
   * produce one alert per participant for a single cause.
   */
  private constructPeerConnection(connectionId: string): RTCPeerConnection | undefined {
    try {
      return new RTCPeerConnection({ iceServers: this.iceServers });
    } catch (error) {
      this.reportPeerConnectionUnavailable(connectionId, error);
      return undefined;
    }
  }

  private reportPeerConnectionUnavailable(connectionId: string, error: unknown): void {
    const name = error instanceof Error ? error.name : typeof error;
    const message = error instanceof Error ? error.message : String(error);

    log.error('[VOIP] This browser refused to construct a peer connection', connectionId, name, message);

    if (this.peerConnectionUnavailableReported) {
      return;
    }

    this.peerConnectionUnavailableReported = true;

    reportRtc('peer-connection-unavailable', {
      connectionId,
      name,
      message,
      // The three things that distinguish the causes: a page that is framed is probably missing an
      // `allow` attribute, a non-native constructor means something replaced it, and a secure
      // context is a precondition for WebRTC at all. `envTap` captures the full picture; these are
      // here so the `rtc` lane is readable on its own.
      framed: typeof window !== 'undefined' && window.self !== window.top,
      nativeConstructor: typeof RTCPeerConnection === 'function' && `${RTCPeerConnection}`.includes('[native code]'),
      secureContext: typeof window !== 'undefined' && window.isSecureContext,
    });

    // The toast belongs to `utils/ui/rtc.ts`, which is where every other VOIP notification lives.
    // Emitting rather than importing `state/toast` also keeps this module's graph closed: it is
    // deliberately narrow (see the sink comment above), and pulling the toast machinery in here
    // drags `components/Toast` and `utils/alerts` into the lambda and vitest graphs with it.
    this.emit('peer:unavailable', connectionId, { name, message });
  }

  /**
   * Build the peer connection for `connectionId`, or `undefined` if this browser will not build one.
   *
   * ## Why this can return nothing
   *
   * Session `312d2e01-…` (2026-09-05): a customer's browser threw
   * `NotAllowedError: Failed to construct 'RTCPeerConnection': RTCPeerConnection is not allowed` —
   * a Permissions-Policy refusal, which an embedding iframe without `allow="camera; microphone"`,
   * an enterprise policy or a WebRTC-blocking extension all produce. Signaling was healthy, the
   * dealer's offer arrived, every ICE candidate arrived. There was simply nothing to put them in.
   *
   * Before this guard the throw escaped `admitPeerFromOffer` as an unhandled rejection, and every
   * subsequent candidate logged `[VOIP] Peer Connection not found` — thirteen times in that session,
   * with no error state, no toast and no retry. The dealer sat in `have-local-offer` until
   * `convergence-failed` eight seconds later. The customer was shown nothing at all.
   *
   * Retrying is pointless: this is a capability refusal, not a transient fault. So the failure is
   * recorded once at `alert` priority (it is worth waking someone), the user is told once (the
   * toast dedups on `notificationId`), and callers get `undefined` and skip the peer.
   */
  private createPeerConnection(connectionId: string, prebuilt?: RTCPeerConnection): RTCPeerConnection | undefined {
    const { remoteConnections } = this.store.data;
    const remoteConnection = remoteConnections[connectionId];
    // AVS-4391: the refresh loop normally keeps this list live. If it has been failing (the
    // /ice-servers endpoint is down), we still have to build the peer — there is no other source
    // of credentials — but say so, because the symptom otherwise is a silent gray tile with
    // nothing in the log tying it to dead TURN credentials.
    if (this.iceServersExpireAt !== null && Date.now() >= this.iceServersExpireAt) {
      log.warn(
        '[VOIP] Building peer connection with EXPIRED TURN credentials; expect no relay candidates',
        connectionId,
        Date.now().toString().slice(-5)
      );
    }
    const peerConnection = prebuilt ?? this.constructPeerConnection(connectionId);

    if (!peerConnection) {
      return undefined;
    }

    log.log('[VOIP] Creating Peer Connection - ', connectionId, Date.now().toString().slice(-5));

    // AVS-4430: the one place every pc is reported (constructed via `constructPeerConnection`, either
    // here or prebuilt by `replacePeerConnection`), so this is the one place a recorder can see every peer — including the ones that never reach `window.voipRtcConnection`
    // because their connect FAILED. `iceServerUrls` says which STUN/TURN servers this pc was even
    // allowed to try; the credentials that ride alongside them are deliberately not passed.
    reportRtc('pc-created', {
      connectionId,
      iceServerUrls: this.iceServers.flatMap((server) =>
        typeof server.urls === 'string' ? [server.urls] : (server.urls ?? [])
      ),
      iceCredentialsExpired: this.iceServersExpireAt !== null && Date.now() >= this.iceServersExpireAt,
      isRebuild: !!remoteConnection?.peerConnection,
    });

    /** Event listeners for the RTC Connection */

    // Handle ICE candidate generation
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        reportRtc('ice-candidate', { connectionId, direction: 'local', candidate: event.candidate });
        // Send the generated ICE candidate to the remote peer via the signaling server
        this.signalerClient.signal({
          action: ERTCEvents.PeerSignal,
          to: [connectionId],
          data: event.candidate,
        });
      } else {
        // A null candidate is end-of-gathering. Silent today, and it is the difference between
        // "we gathered nothing" and "we are still gathering".
        reportRtc('ice-gathering-complete', {
          connectionId,
          iceGatheringState: peerConnection.iceGatheringState,
        });
      }
    };

    // Handle incoming media streams from the remote peer
    peerConnection.ontrack = (event: RTCTrackEvent) => {
      const stream = event.streams[0];
      const track = event.track;
      log.log(`[VOIP] received a track from peer...`, connectionId, track);
      const remoteConnections = this.store.get('remoteConnections');
      const remoteConnection = remoteConnections[connectionId];
      if (remoteConnection) {
        // Create a new MediaStream with only live tracks to force React re-render.
        // Chrome requires srcObject reassignment to pick up new tracks added to a stream
        // after removeTrack+addTrack renegotiation (same stream ref = no React update).
        // When a track arrives via replaceTrack renegotiation, event.streams[0] may be undefined
        // (SDP has msid:-). Fall back to combining existing stream tracks with the new track.
        let sourceTracks: MediaStreamTrack[];
        if (stream) {
          sourceTracks = stream.getTracks();
        } else if (remoteConnection.stream) {
          sourceTracks = [...remoteConnection.stream.getTracks().filter((t) => t.id !== track.id), track];
        } else {
          sourceTracks = [track];
        }
        const activeStream = new MediaStream(sourceTracks.filter((t) => t.readyState !== 'ended'));
        // Standby suppression: disable tracks BEFORE the store write so stream containers
        // React already holds (same track objects) go silent in the same tick.
        this.applyIncomingMediaPolicy(activeStream);
        log.log(
          `[VOIP] ontrack: creating new MediaStream with`,
          activeStream.getTracks().length,
          'live tracks for',
          connectionId
        );
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [connectionId]: {
              ...remoteConnection,
              stream: activeStream,
            },
          },
        });

        // AVS-4430 media-health: the moment a stream-less remote stops being stream-less. Paired
        // with the `peer-added` record, this bounds how long a tile was gray — the strong
        // transmission-failure signal, and free to read.
        reportRtc('stream-attached', {
          connectionId,
          trackKind: track.kind,
          trackId: track.id,
          trackMuted: track.muted,
          trackEnabled: track.enabled,
          trackReadyState: track.readyState,
          streamTracks: activeStream.getTracks().length,
          hadStream: !!remoteConnection.stream,
        });

        // Add stream to speaker detector when it's an audio track
        if (track.kind === 'audio' && activeStream) {
          this.speakerDetector.addStream(connectionId, activeStream);
        }
      }
    };

    // AVS-4302: complete perfect-negotiation. onnegotiationneeded is the single offer engine for
    // RENEGOTIATION (a track/direction change, or restartIce) — driveNegotiation offers only from a
    // clean stable state with the makingOffer guard, so it never collides with an in-flight offer.
    // Initial offers stay explicit + getInitiator-gated in prepareConnectionAndOffer (glare-free
    // common case). The signaling/ICE hooks drive recovery: stable clears the awaiting-answer timer;
    // ICE failure escalates to restartIce.
    //
    // AVS-4430: each of the four transitions is reported BEFORE its handler runs, so a recording
    // shows the state the app reacted to rather than the state it left behind.
    peerConnection.onnegotiationneeded = () => {
      reportRtc('negotiation-needed', { connectionId, signalingState: peerConnection.signalingState });
      void this.driveNegotiation(connectionId);
    };
    peerConnection.onsignalingstatechange = () => {
      reportRtc('signaling-state', { connectionId, signalingState: peerConnection.signalingState });
      this.handleSignalingStateChange(connectionId);
    };
    peerConnection.oniceconnectionstatechange = () => {
      reportRtc('ice-state', {
        connectionId,
        iceConnectionState: peerConnection.iceConnectionState,
        connectionState: peerConnection.connectionState,
        iceRestarts: this.iceRestartCounts[connectionId] ?? 0,
      });
      this.handleIceConnectionStateChange(connectionId);
    };

    // Close → remove; DTLS-failed → rebuild (see handleConnectionStateChange).
    peerConnection.onconnectionstatechange = () => {
      reportRtc('connection-state', {
        connectionId,
        connectionState: peerConnection.connectionState,
        iceConnectionState: peerConnection.iceConnectionState,
        transportRebuilds: this.transportRebuilds[connectionId] ?? 0,
      });
      this.handleConnectionStateChange(connectionId);
    };

    // Update the state to associate the peer connection with the remote connection.
    log.log(
      '[VOIP] Peer Connection Created - ',
      connectionId,
      'current remote connections',
      remoteConnections,
      Date.now()
    );
    this.store.setValues({
      remoteConnections: {
        ...remoteConnections,
        [connectionId]: {
          ...remoteConnection,
          peerConnection,
          // AVS-4392: this entry just got a genuinely fresh transport, so any previous give-up no
          // longer describes it. Defensive today — a flagged peer is turned away at the top of
          // rebuildPeerTransport, so nothing currently reaches here for one — but it keeps the
          // flag tied to the transport it describes rather than to the entry that outlives it.
          transportFailed: false,
          // AVS-4458: the same rule for the stalled-video flag — it described the old transport.
          videoStalled: false,
        },
      },
    });

    return peerConnection;
  }

  private async prepareConnectionAndOffer(connectionId: string, receiveOnly = false): Promise<void> {
    const { me, remoteConnections } = this.store.data;

    if (remoteConnections) {
      // Create a new RTCPeerConnection with the specified ICE servers
      const peerConnection = remoteConnections[connectionId]?.peerConnection || this.createPeerConnection(connectionId);

      if (!peerConnection) {
        return;
      }

      // The offer below is still sent when we are the lexical initiator, tracks or not.
      this.attachLocalMedia(peerConnection, connectionId, receiveOnly);

      const initiator = this.getInitiator(me.connectionId, connectionId);
      const iAmTheInitiator = initiator === me.connectionId;
      log.log('[VOIP] Initiator is ', initiator, Date.now().toString().slice(-5));

      if (iAmTheInitiator) {
        // Same guard as driveNegotiation: a re-publish/demotion pass can hit a pc that is
        // mid-handshake; offering there throws InvalidStateError as an unhandled rejection.
        // onnegotiationneeded/forceReoffer pick up whatever this skip leaves undone.
        if (peerConnection.signalingState === 'stable' && !this.makingOffer[connectionId]) {
          this.sendOffer(peerConnection, connectionId).catch((err: unknown) => {
            log.log('[VOIP] Initial offer failed for', connectionId, err, Date.now().toString().slice(-5));
          });
        } else {
          log.log('[VOIP] Skipping offer (pc not stable / offer in flight)', connectionId);
        }
      } else {
        log.log('[VOIP] Not creating offer for ', connectionId, me, Date.now().toString().slice(-5));
        // Never-negotiated pair: the initiator owes us the first offer — arm the waiting-side
        // deadline in case it never arrives. A live pair (promotion/demotion re-pass) needs no
        // watchdog: renegotiation there is event-driven via onnegotiationneeded.
        if (!peerConnection.remoteDescription) {
          this.armWaitingOfferWatchdog(connectionId);
        }
      }

      this.armConvergenceReport(connectionId);
    }
  }

  /**
   * Add each track from the user's stream to the peer connection if available.
   * AVS-4302: `receiveOnly` forces the recvonly path even when a local stream exists, so a standby
   * (negotiating purely to RECEIVE the master's loopback) never adds tracks and can never publish a
   * second dealer stream — even if a camera-grab race left it a local stream.
   */
  private attachLocalMedia(
    peerConnection: RTCPeerConnection,
    connectionId: string,
    receiveOnly: boolean,
    placed?: ReadonlySet<MediaStreamTrack>
  ): void {
    if (this.store.get('me').stream && !receiveOnly) {
      log.log('[VOIP] Sharing my stream to', connectionId, Date.now().toString().slice(-5));
      this.startStreamingMe(peerConnection, placed);
      this.signalMutedState(connectionId, this.nextMutedSeq());
    } else {
      // No local stream (or receive-only) - receive-only mode
      log.log('[VOIP] No outgoing stream - receive-only mode for', connectionId, Date.now().toString().slice(-5));
    }
  }

  private getInitiator = (a: string, b: string) => {
    return a > b ? a : b;
  };

  // AVS-4302: perfect-negotiation role. The impolite peer (higher connectionId — the same one
  // getInitiator picks) keeps its offer on a collision; the polite peer (lower) rolls back and
  // answers. Exactly one of any pair is polite. Returns false when our cid is unknown so we never
  // guess a role (handleOfferSignals handles the empty-cid case explicitly before calling this).
  private isPolite = (myConnectionId: string, peerConnectionId: string) =>
    !!myConnectionId && myConnectionId < peerConnectionId;

  private removePeer = (connectionId: string) => {
    const remoteConnections = this.store.get('remoteConnections');
    const departing = remoteConnections[connectionId];
    reportRtc('peer-removed', {
      connectionId,
      hasStream: !!departing?.stream,
      transportFailed: !!departing?.transportFailed,
      metaPending: !!departing?.metaPending,
      connectionState: departing?.peerConnection?.connectionState ?? '',
      iceConnectionState: departing?.peerConnection?.iceConnectionState ?? '',
      remaining: Object.keys(remoteConnections).length - 1,
    });
    const filteredRemoteConnections = Object.keys(remoteConnections).reduce(
      (acc: Record<string, TRTCConnection>, key: string) => {
        if (key !== connectionId) {
          acc[key] = remoteConnections[key];
        }
        return acc;
      },
      {}
    );
    this.store.setValues({ remoteConnections: filteredRemoteConnections });

    // AVS-4302: drop this peer's glare-resolution flags so a reused connectionId can't inherit a
    // stale role/ignore state. Cancel its recovery timers and delete its retry/restart budgets too,
    // so a takeover that re-adds the SAME connectionId is not born with an exhausted budget.
    delete this.makingOffer[connectionId];
    delete this.ignoringOffer[connectionId];
    delete this.highestSdpSeq[connectionId];
    delete this.divergentHeals[connectionId];
    if (this.renegotiationTimers[connectionId]) {
      clearTimeout(this.renegotiationTimers[connectionId]);
      delete this.renegotiationTimers[connectionId];
    }
    this.clearIceGraceTimer(connectionId);
    this.clearConvergenceTimer(connectionId);
    delete this.renegotiationRetries[connectionId];
    delete this.iceRestartCounts[connectionId];
    delete this.iceGiveUpReported[connectionId];
    delete this.transportRebuilds[connectionId];
    delete this.silentTransportEscalations[connectionId];
    delete this.stalledVideoEscalations[connectionId];
    delete this.stalledVideoGiveUpSent[connectionId];
    delete this.collisionReplacements[connectionId];
    delete this.collisionExhaustedReported[connectionId];
    delete this.deadReceiverRebuilds[connectionId];
    delete this.deadReceiverGiveUpSent[connectionId];
    this.clearDeadReceiverCheck(connectionId);
    delete this.videoOffHonored[connectionId];
    // AVS-4392: the candidate queue was the one per-peer map this cleanup missed. Harmless while
    // candidates were only ever queued pre-first-negotiation; with restart candidates now retained
    // for retry, a peer departing mid-restart would otherwise leave its queue in the map for the
    // rest of the session, re-walked forever by the no-arg flush.
    delete this.pendingCandidates[connectionId];

    this.speakerDetector.removeStream(connectionId);
    // If no more connections, stop the detector
    if (Object.keys(filteredRemoteConnections).length === 0) {
      this.speakerDetector.stop();
    }

    log.log(
      '[VOIP] Connection Removed - ',
      connectionId,
      this.store.data.remoteConnections,
      Date.now().toString().slice(-5)
    );
  };

  /**
   * AVS-4440: the next `mutedSeq`. Wall-clock based so a rebuilt client usually starts above its
   * predecessor, and strictly above the last one whatever the clock does.
   */
  private nextMutedSeq(): number {
    this.lastMutedSeq = Math.max(Date.now(), this.lastMutedSeq + 1);
    return this.lastMutedSeq;
  }

  private signalMutedState = (connectionId: string, mutedSeq: number) => {
    this.signalerClient.signal({
      action: ERTCEvents.StreamUpdate,
      to: [connectionId],
      data: {
        audioMuted: !myCameraStream().get('isAudioEnabled'),
        videoMuted: !myCameraStream().get('isVideoEnabled'),
        mutedSeq,
      },
    });
  };

  /** One fan-out is one state, so every peer gets the same `mutedSeq`. */
  private signalMutedStateToAllPeers(): void {
    const mutedSeq = this.nextMutedSeq();
    Object.values(this.store.get('remoteConnections')).forEach(({ connectionId }) => {
      this.signalMutedState(connectionId, mutedSeq);
    });
  }

  public toggleMedia = (type: 'audio' | 'video'): VoipMediaState | undefined => {
    const { me } = this.store.data;
    // Cannot toggle media if no local stream exists (receive-only mode)
    if (!me.stream) {
      log.warn('[VOIP] Cannot toggle media - no local stream (receive-only mode)');
      return;
    }

    let microphoneEnabled = myCameraStream().get('isAudioEnabled');
    let cameraEnabled = myCameraStream().get('isVideoEnabled');

    switch (type) {
      case 'audio':
        microphoneEnabled = myCameraStream().toggleAudio();
        break;
      case 'video':
        cameraEnabled = myCameraStream().toggleVideo();
        // AVS-4458: the user turned the camera back on, so each peer may ask once more.
        if (cameraEnabled) {
          this.videoOffHonored = {};
        }
        break;
      default:
        break;
    }
    // AVS-4440: no per-peer send here. The flag change itself re-signals every peer, through the
    // camera `change` listener in registerMyVideoStreamListeners.
    return { microphoneEnabled, cameraEnabled };
  };
}
